/**
 * Colour Quick Order — design token amr.colour-quick-order, version 2026.09.08
 *
 * A five step trade quick-order flow: pick a brand, enter tube quantities across
 * that brand's whole shade range, add developer and bleach, optionally top up
 * retail, review, and add the lot to the cart in one request.
 *
 * Products are not rendered by Liquid. colour-quick-order.liquid resolves which
 * collection feeds each step for each brand; this file fetches that collection
 * from the Ajax product feed the first time a shopper reaches the step, and
 * caches it for the session. Quantities live in one { variantId: qty } map and
 * every total is derived from it, so nothing is stored twice.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Shade colour generation
   *
   * The catalogue does not carry swatch hexes, so they are derived from the
   * shade code: the level digit sets OKLCh lightness, the tone digits set
   * chroma and hue. A product tag `swatch:#rrggbb` overrides the result for
   * any shade whose generated colour is wrong.
   * ------------------------------------------------------------------ */

  var TONE = {
    '0': [0.045, 70], '00': [0.05, 65], '01': [0.03, 240], '02': [0.03, 130],
    '03': [0.075, 72], '05': [0.09, 22], '07': [0.08, 55], '1': [0.02, 255],
    '11': [0.022, 250], '12': [0.025, 245], '13': [0.05, 88], '16': [0.05, 295],
    '17': [0.05, 60], '18': [0.03, 285], '2': [0.05, 140], '21': [0.045, 305],
    '26': [0.045, 78], '28': [0.06, 215], '3': [0.1, 82], '30': [0.11, 84],
    '31': [0.07, 88], '34': [0.12, 58], '35': [0.06, 48], '38': [0.07, 80],
    '4': [0.14, 42], '41': [0.1, 35], '43': [0.13, 50], '44': [0.18, 30],
    '45': [0.15, 35], '46': [0.16, 30], '5': [0.1, 18], '55': [0.11, 18],
    '6': [0.09, 320], '61': [0.07, 310], '62': [0.12, 8], '65': [0.11, 340],
    '66': [0.19, 27], '666': [0.2, 30], '7': [0.06, 62], '71': [0.035, 80],
    '73': [0.07, 74], '75': [0.08, 30], '77': [0.07, 60], '8': [0.03, 300],
    '81': [0.028, 295], '82': [0.02, 300], '86': [0.05, 305], '88': [0.08, 250],
    '89': [0.03, 290], '91': [0.025, 250], '96': [0.045, 300], '97': [0.03, 65],
    '98': [0.025, 290]
  };

  var SERIES_LABEL = {
    '0': 'Naturals', '00': 'Intense naturals', '01': 'Natural ash',
    '02': 'Natural matte', '03': 'Natural gold', '05': 'Natural mahogany',
    '07': 'Natural brown', '1': 'Ash', '11': 'Intense ash', '12': 'Ice',
    '13': 'Beige', '16': 'Ash violet', '17': 'Ash brown', '18': 'Ash pearl',
    '2': 'Matte / violet', '21': 'Iridescent ash', '26': 'Champagne',
    '28': 'Matte blue', '3': 'Gold', '30': 'Gold', '31': 'Gold ash',
    '34': 'Gold red', '35': 'Chocolate', '38': 'Gold pearl', '4': 'Copper',
    '41': 'Red ash', '43': 'Red gold', '44': 'Intense red', '45': 'Red mahogany',
    '46': 'Copper red', '5': 'Mahogany', '55': 'Intense mahogany', '6': 'Violet',
    '61': 'Violet ash', '62': 'Burgundy', '65': 'Violet mahogany',
    '66': 'Intense red / violet', '666': 'Flame red', '7': 'Brown',
    '71': 'Brown ash', '73': 'Brown gold', '75': 'Brown mahogany',
    '77': 'Intense brown', '8': 'Pearl / cappuccino', '81': 'Pearl ash',
    '82': 'Pearl matte', '86': 'Pearl violet', '88': 'Intense blue',
    '89': 'Pearl cendre', '91': 'Ash blonde', '96': 'Cendre violet',
    '97': 'Cendre brown', '98': 'Cendre pearl',
    special: 'Special & extra-special blondes',
    corr: 'Correctors & boosters',
    other: 'Other shades'
  };

  /* Display order for series groups. */
  var SERIES_ORDER = [
    '0', '00', '01', '02', '03', '05', '07', '1', '11', '12', '13', '16', '17',
    '18', '2', '21', '26', '28', '3', '30', '31', '34', '35', '38', '4', '41',
    '43', '44', '45', '46', '5', '55', '6', '61', '62', '65', '66', '666', '7',
    '71', '73', '75', '77', '8', '81', '82', '86', '88', '89', '91', '96', '97',
    '98', 'special', 'corr', 'other'
  ];

  var FIXED_SWATCH = {
    CB: [0.55, 0.13, 250], CV: [0.5, 0.16, 310], CS: [0.72, 0.02, 260],
    CC: [0.9, 0.005, 90], 'C.44': [0.62, 0.16, 45], 'R.66': [0.5, 0.2, 27]
  };

  /* Bleach and lightener blocks, keyed off words in the product title. */
  var BLEACH_TINT = [
    [/plex|bond/i, [0.86, 0.03, 250]],
    [/blue/i, [0.68, 0.09, 250]],
    [/violet|purple/i, [0.62, 0.12, 310]],
    [/clay/i, [0.72, 0.04, 70]],
    [/ammonia.?free|gentle/i, [0.88, 0.02, 120]],
    [/white|dust.?free|multi.?blonde/i, [0.93, 0.012, 90]]
  ];

  var ALL_KINDS = ['colour', 'dev', 'bleach', 'retail', 'charts', 'accessories'];
  /* Everything on step 4 — retail, colour charts and brand accessories — counts as
     one line on the order summary and one card in the review. */
  var RETAIL_KINDS = ['retail', 'charts', 'accessories'];
  var STEP_LABELS = ['Brands', 'Colours', 'Developers & Lighteners', 'Retail', 'Review'];
  var NEXT_LABELS = [
    'Choose a brand',
    'Continue to developers',
    'Continue to retail',
    'Review order',
    'Add all to cart'
  ];
  var VIEW_OPTIONS = [['grid', 'Series'], ['rows', 'List'], ['acc', 'Series List']];

  /** OKLCh -> sRGB hex, so the chips render everywhere and can go in an inline style. */
  function oklchToHex(L, C, h) {
    var hr = (h * Math.PI) / 180;
    var a = C * Math.cos(hr);
    var b = C * Math.sin(hr);
    var l_ = L + 0.3963377774 * a + 0.2158037573 * b;
    var m_ = L - 0.1055613458 * a - 0.0638541728 * b;
    var s_ = L - 0.0894841775 * a - 1.291485548 * b;
    var l = l_ * l_ * l_;
    var m = m_ * m_ * m_;
    var s = s_ * s_ * s_;
    var rgb = [
      4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
      -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
      -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
    ];
    return (
      '#' +
      rgb
        .map(function (v) {
          v = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(Math.max(v, 0), 1 / 2.4) - 0.055;
          var byte = Math.round(Math.min(1, Math.max(0, v)) * 255);
          return (byte < 16 ? '0' : '') + byte.toString(16);
        })
        .join('')
    );
  }

  function swatchForCode(code) {
    if (!code) return '#f0efed';
    var fixed = FIXED_SWATCH[code.toUpperCase()];
    if (fixed) return oklchToHex(fixed[0], fixed[1], fixed[2]);
    var parts = String(code).split(/[./\-]/);
    var level = Math.min(parseFloat(parts[0]) || 6, 12);
    var tone = TONE[parts[1]] || TONE['0'];
    var L = Math.min(0.155 + (level - 1) * 0.067, 0.93);
    return oklchToHex(L, tone[0] * (0.55 + L * 0.6), tone[1]);
  }

  var HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
  var RGB = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d+)\s*)?\)$/i;
  var COLOUR_WORD = /^[a-z]{3,20}$/i;

  /**
   * A `swatch:` product tag is merchant-controlled text that ends up inside a
   * style attribute, so only a plain hex or a bare colour keyword is allowed
   * through. Anything else falls back to the generated colour.
   */
  function safeColour(value, fallback) {
    if (!value) return fallback;
    var trimmed = String(value).trim();
    if (HEX.test(trimmed)) return trimmed;
    if (RGB.test(trimmed)) return trimmed;
    if (HEX.test('#' + trimmed)) return '#' + trimmed;
    if (COLOUR_WORD.test(trimmed)) return trimmed.toLowerCase();
    return fallback;
  }

  /**
   * What to paint in a shade chip. A real variant photo beats a generated
   * colour, so it wins whenever the catalogue has one. Restricted to URLs that
   * cannot break out of the style attribute.
   */
  function swatchBackground(item) {
    var colour = (item && item.hex) || '#f0efed';
    var image = item && item.image ? String(item.image) : '';
    /* Single quotes: this lands inside a double-quoted style attribute. The
       generated colour rides along behind the image in the same shorthand, so a
       photo that 404s leaves a plausible swatch rather than an empty box —
       background images give us no error event to hook. */
    if (image && /^(?:https?:)?\/\/[^"'()\s]+$/.test(image)) return "url('" + image + "') " + colour;
    return colour;
  }

  function swatchForBleach(title) {
    for (var i = 0; i < BLEACH_TINT.length; i++) {
      if (BLEACH_TINT[i][0].test(title)) {
        var t = BLEACH_TINT[i][1];
        return oklchToHex(t[0], t[1], t[2]);
      }
    }
    return oklchToHex(0.9, 0.008, 90);
  }

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function toCents(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Math.round(value * 100);
    return Math.round(parseFloat(String(value).replace(/[^0-9.\-]/g, '')) * 100) || 0;
  }

  /** Shopify's money formatter, driven by shop.money_format from Liquid. */
  function formatMoney(cents, format) {
    var formatString = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholder);
    if (!match) return formatString;

    function withDelimiters(number, precision, thousands, decimal) {
      if (isNaN(number) || number == null) return '0';
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return formatString.replace(placeholder, value);
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /**
   * Whether two vendor strings name the same brand. Exact matching is too brittle
   * here: a brand configured as "Wella" has products filed under "Wella
   * Professionals", and its own lighteners would then be filed under "other
   * brands". One being a prefix of the other is close enough.
   */
  /**
   * When one collection feeds both the developer and the bleach step — Wella's
   * `wella-professionals` holds peroxide and Blondor together — the lines have to
   * be split by what they are, or each step shows the other's stock. Only applied
   * when the two steps genuinely name the same source.
   */
  function splitDevBleach(lines, kind, patterns) {
    var bleach = new RegExp(patterns.bleach, 'i');
    var dev = new RegExp(patterns.dev, 'i');
    return lines.filter(function (line) {
      var text = (line.name || '') + ' ' + (line.size || '') + ' ' + (line.vol || '');
      if (bleach.test(text)) return kind === 'bleach';
      return kind === 'dev' && dev.test(text);
    });
  }

  function normaliseVendor(value) {
    var text = String(value || '').trim().toLowerCase();
    /* "L'Oreal" and "L'Oréal Professionnel" are the same brand as far as grouping
       is concerned, so fold accents away before comparing. */
    return text.normalize ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : text;
  }

  function vendorMatches(a, b) {
    var left = normaliseVendor(a);
    var right = normaliseVendor(b);
    if (!left || !right) return false;
    return left === right || left.indexOf(right) === 0 || right.indexOf(left) === 0;
  }

  function dedupeById(lines) {
    var seen = {};
    return lines.filter(function (line) {
      if (seen[line.id]) return false;
      seen[line.id] = true;
      return true;
    });
  }

  function plural(count, one, many) {
    return count === 1 ? one : many;
  }

  /* ------------------------------------------------------------------ *
   * Catalogue parsing
   * ------------------------------------------------------------------ */

  var SIZE_TAIL = /\s*[·|,-]?\s*\d+(?:\.\d+)?\s*(?:ml|l|g|kg|oz)\b\.?$/i;
  var CODE_TOKEN = /^(?:[0-9]{1,2}(?:[./\-][0-9]{1,3}){0,2}|[A-Z]{1,2}(?:[./][0-9]{1,3})?)$/;

  function tagValue(tags, prefix) {
    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i]);
      if (tag.toLowerCase().indexOf(prefix) === 0) return tag.slice(prefix.length).trim();
    }
    return '';
  }

  function hasTag(tags, name) {
    if (!name) return false;
    var wanted = name.toLowerCase();
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).toLowerCase() === wanted) return true;
    }
    return false;
  }

  /**
   * Split a colour product title into { code, name }.
   *
   * Tries, in order: an explicit `code:8.1` product tag, a "Colour"/"Shade"
   * option value that looks like a code, then the title itself with the brand
   * prefix and trailing size removed. Falls back to the SKU.
   */
  function parseShade(product, variant, brand) {
    var tags = product.tags || [];
    var tagged = tagValue(tags, 'code:') || tagValue(tags, 'shade:');
    var title = String(product.title || '');

    var stripped = title;
    [brand.title, brand.name + ' ' + brand.range, brand.name].forEach(function (prefix) {
      if (prefix && stripped.toLowerCase().indexOf(prefix.toLowerCase()) === 0) {
        stripped = stripped.slice(prefix.length).trim();
      }
    });
    stripped = stripped.replace(/^[-–—·:]\s*/, '').replace(SIZE_TAIL, '').trim();

    if (tagged) {
      var withoutCode = stripped.replace(new RegExp('^' + tagged.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b\\s*'), '');
      return { code: tagged, name: withoutCode || stripped };
    }

    var optionValue = (variant.option1 || '').trim();
    if (optionValue && CODE_TOKEN.test(optionValue)) {
      return { code: optionValue, name: stripped.replace(optionValue, '').trim() || stripped };
    }

    var leading = stripped.match(/^((?:[0-9]{1,2}(?:[./\-][0-9]{1,3}){1,2})|(?:[0-9]{1,2}[./\-][0-9]{1,3})|(?:[A-Z]{1,2}(?:\.[0-9]{1,3})?))\s+(.+)$/);
    if (leading) return { code: leading[1], name: leading[2] };

    /* Code somewhere mid-title: everything after it is the shade name. */
    var anywhere = stripped.match(/(?:^|\s)([0-9]{1,2}[./\-][0-9]{1,3}(?:[./\-][0-9]{1,3})?)(?:\s|$)/);
    if (anywhere) {
      var at = stripped.indexOf(anywhere[1]);
      var after = stripped.slice(at + anywhere[1].length).trim();
      return { code: anywhere[1], name: after || stripped.slice(0, at).trim() };
    }

    return { code: variant.sku || stripped.slice(0, 8), name: stripped };
  }

  function seriesKey(code) {
    if (!code) return 'other';
    if (/^[A-Za-z]/.test(code)) return 'corr';
    var parts = String(code).split(/[./\-]/);
    var level = parseFloat(parts[0]);
    if (isNaN(level)) return 'other';
    if (level >= 11) return 'special';
    return parts[1] || '0';
  }

  function shadeSortValue(code) {
    var parts = String(code || '').split(/[./\-]/);
    var level = parseFloat(parts[0]);
    if (isNaN(level)) return [999, 0];
    return [level, parseFloat(parts[1]) || 0];
  }

  /** Volume for a developer card: "20 Vol", "Zero Lift", or the variant label. */
  /**
   * Developers read in a sensible order rather than collection order: 6, 13, 30,
   * 40 Vol first, then the emulsions by strength, then activators, then whatever
   * parsed as none of those.
   */
  function developerSortValue(line) {
    var text = (line.vol || '') + ' ' + (line.name || '');
    if (/zero/i.test(text)) return [0, 0];
    var vol = text.match(/(\d+(?:\.\d+)?)\s*vol\b/i);
    if (vol) return [0, parseFloat(vol[1])];
    var pct = text.match(/(\d+(?:\.\d+)?)\s*%/);
    if (pct) return [1, parseFloat(pct[1])];
    if (/activat/i.test(text)) return [2, 0];
    return [3, 0];
  }

  function developerVolume(product, variant, multiVariant) {
    var haystack = (multiVariant ? variant.title + ' ' : '') + product.title;
    var vol = haystack.match(/(\d+(?:\.\d+)?)\s*(?:vol|volume)\b/i);
    if (vol) return vol[1] + ' Vol';
    if (/zero/i.test(haystack)) return 'Zero Lift';
    if (/activat/i.test(haystack)) return 'Activator';
    if (multiVariant && variant.title && variant.title !== 'Default Title') return variant.title;
    return product.title;
  }

  function sizeLabel(product, variant, multiVariant) {
    var source = multiVariant && variant.title && variant.title !== 'Default Title' ? variant.title : product.title;
    var size = source.match(/(\d+(?:\.\d+)?)\s*(ml|l|g|kg)\b/i);
    return size ? size[1] + size[2].toUpperCase() : '';
  }

  /* ------------------------------------------------------------------ *
   * The element
   * ------------------------------------------------------------------ */

  var STORAGE_PREFIX = 'cqo:';

  class ColourQuickOrderElement extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      var configEl = this.querySelector('[data-cqo-config]');
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (error) {
        console.error('[colour-quick-order] config could not be parsed', error);
        return;
      }

      this.settings = this.config.settings || {};
      this.routes = this.config.routes || {};
      this.brands = this.config.brands || [];
      this.lastOrder = this.config.lastOrder || {};

      this.main = this.querySelector('[data-cqo-main]');
      this.rail = this.querySelector('[data-cqo-rail]');
      this.summary = this.querySelector('[data-cqo-summary]');
      this.bar = this.querySelector('[data-cqo-bar]');

      /* Step 1 arrives rendered from Liquid — keep it rather than rebuild it. */
      this.brandStepHTML = this.main.innerHTML;

      this.catalogue = {};
      this.loading = {};
      this.errors = {};
      this.truncated = {};
      this.fetchCache = {};
      /* What the cart already holds, per variant. Steppers stop at what is left
         rather than letting a shopper build an order the cart will refuse. */
      this.cartQty = {};

      this.state = Object.assign(
        {
          step: 0,
          brand: null,
          qty: {},
          view: this.settings.listView || 'grid',
          q: '',
          group: 'all',
          open: {},
          openDev: {},
          openBleach: {},
          paste: '',
          pasteOpen: false,
          pasteResult: '',
          done: false,
          confirmation: null
        },
        this.readStoredState()
      );

      this.onSearch = debounce(
        function (value) {
          this.state.q = value;
          this.renderList(true);
        }.bind(this),
        200
      );

      /* Images hot-linked from the CDN can 404; swap in the marked fallback
         rather than leaving a broken tile. `error` does not bubble, so capture. */
      this.addEventListener('error', this.handleImageError.bind(this), true);
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('input', this.handleInput.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));

      /* A brand row whose colour handle does not resolve is dropped by the Liquid
         rather than shown as a dead tile — say so where a developer will see it. */
      if (this.config.unresolved) {
        console.warn(
          '[colour-quick-order] these brand rows were skipped, their colour handle ' +
          'matched no product or collection: ' + this.config.unresolved
        );
      }

      /* A stray "|" in the hand-edited brand table shifts every later column along,
         and the steps then quietly load the wrong thing. Say so loudly. */
      if (this.config.suspectRows) {
        console.warn(
          '[colour-quick-order] these brand rows look mis-columned — check for a ' +
          'stray "|": ' + this.config.suspectRows
        );
      }

      /* What is already in the cart decides how much headroom each stepper has.
         Fetched alongside the first paint rather than before it, so a slow cart
         never holds up the flow; the steppers re-render once it lands. */
      this.loadCartQuantities().then(
        function () {
          if (Object.keys(this.cartQty).length) this.render();
        }.bind(this)
      );

      /* go() renders and, for steps 2–5, kicks off the fetch a restored step needs. */
      this.go(this.state.step);
    }

    /* -------------------------------------------------------------- *
     * State
     * -------------------------------------------------------------- */

    get storageKey() {
      return STORAGE_PREFIX + window.location.pathname;
    }

    readStoredState() {
      try {
        var raw = sessionStorage.getItem(this.storageKey);
        if (!raw) return {};
        var saved = JSON.parse(raw);
        var known = this.brands.some(
          function (brand) {
            return brand.id === saved.brand;
          }
        );
        if (!known) return {};
        return { step: saved.step || 0, brand: saved.brand, qty: saved.qty || {}, view: saved.view || this.settings.listView };
      } catch (error) {
        return {};
      }
    }

    clearStoredState() {
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        /* Nothing to clear. */
      }
    }

    storeState() {
      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({ step: this.state.step, brand: this.state.brand, qty: this.state.qty, view: this.state.view })
        );
      } catch (error) {
        /* Private browsing — the flow still works, it just will not survive a reload. */
      }
    }

    brand() {
      var id = this.state.brand;
      for (var i = 0; i < this.brands.length; i++) {
        if (this.brands[i].id === id) return this.brands[i];
      }
      return null;
    }

    items(kind) {
      var brand = this.brand();
      if (!brand) return [];
      var byBrand = this.catalogue[brand.id];
      return (byBrand && byBrand[kind]) || [];
    }

    /** Every loaded line across every brand, indexed by variant id. */
    index() {
      var all = {};
      Object.keys(this.catalogue).forEach(
        function (brandId) {
          ALL_KINDS.forEach(
            function (kind) {
              this.itemsFor(brandId, kind).forEach(function (item) {
                all[item.id] = item;
              });
            }.bind(this)
          );
        }.bind(this)
      );
      return all;
    }

    /**
     * Every picked line across every brand loaded this session, deduplicated by
     * variant. The developer step lists other brands' ranges, and several brands
     * share the one bleach collection, so totals cannot be scoped to the selected
     * brand without either losing lines or counting them twice.
     */
    lines(kinds) {
      var qty = this.state.qty;
      var seen = {};
      var out = [];
      Object.keys(this.catalogue).forEach(
        function (brandId) {
          kinds.forEach(
            function (kind) {
              (this.catalogue[brandId][kind] || []).forEach(function (item) {
                if (qty[item.id] > 0 && !seen[item.id]) {
                  seen[item.id] = true;
                  out.push({ item: item, qty: qty[item.id] });
                }
              });
            }.bind(this)
          );
        }.bind(this)
      );
      return out;
    }

    /** The loaded lines for one step of one brand. */
    itemsFor(brandId, kind) {
      var byBrand = this.catalogue[brandId];
      return (byBrand && byBrand[kind]) || [];
    }

    /**
     * Brands whose developer range is worth offering alongside the selected one.
     * Several ranges of the same brand share a developer collection — Wella's
     * Koleston and Illumina both draw on wella-professionals — so this is
     * deduplicated by source, not by brand, and skips anything already shown at
     * the top of the step.
     */
    otherDevBrands() {
      var current = this.brand();
      var seen = {};
      if (current) seen[this.sourceHandles(current, 'dev')] = true;

      return this.brands.filter(
        function (brand) {
          if (current && brand.id === current.id) return false;
          var step = (brand.steps || {}).dev || {};
          if (!(step.products || []).length && !(step.collections || []).length) return false;
          var signature = this.sourceHandles(brand, 'dev');
          if (seen[signature]) return false;
          seen[signature] = true;
          return true;
        }.bind(this)
      );
    }

    sum(kinds) {
      return this.lines(kinds).reduce(function (total, line) {
        return total + line.item.price * line.qty;
      }, 0);
    }

    units(kinds) {
      return this.lines(kinds).reduce(function (total, line) {
        return total + line.qty;
      }, 0);
    }

    money(cents) {
      return formatMoney(cents, this.settings.moneyFormat);
    }

    /** The loaded line for a variant id, whichever step it belongs to. */
    lineFor(id) {
      return this.index()[id] || null;
    }

    /**
     * Snap a quantity to the variant's B2B quantity rule. Shopify rejects the
     * whole add-to-cart batch over a broken rule, so it is enforced here rather
     * than discovered at checkout.
     */
    /**
     * The most of a line a shopper can still take: the smaller of its B2B order
     * maximum and its remaining stock, less whatever is already in their cart.
     * Returns 0 when nothing caps it — the collection path has neither figure,
     * because the Ajax product feed carries no inventory count or quantity rule.
     */
    ceiling(line) {
      if (!line) return 0;
      var caps = [];
      if (line.max > 0) caps.push(line.max);
      if (line.avail > 0) caps.push(line.avail);
      if (!caps.length) return 0;
      var cap = Math.min.apply(null, caps);
      return Math.max(0, cap - (this.cartQty[String(line.id)] || 0));
    }

    /** True when a line is capped but the cart already holds all of it. */
    atCartLimit(line) {
      if (!line) return false;
      /* Only ever tested with > 0: collection-sourced lines carry neither figure,
         and `undefined <= 0` is false, which would lock every one of them. */
      var capped = line.max > 0 || line.avail > 0;
      if (!capped) return false;
      return this.ceiling(line) === 0;
    }

    /** Read what is in the cart so the ceilings above mean something. */
    async loadCartQuantities() {
      try {
        var cart = await fetch((this.routes.cart || '/cart') + '.js', {
          headers: { Accept: 'application/json' }
        }).then(function (response) {
          return response.json();
        });
        var held = {};
        (cart.items || []).forEach(function (line) {
          var id = String(line.id);
          held[id] = (held[id] || 0) + line.quantity;
        });
        this.cartQty = held;
      } catch (error) {
        /* A ceiling is a courtesy. Without it the steppers simply do not cap. */
        this.cartQty = {};
      }
    }

    clampQty(id, value) {
      var wanted = Math.max(0, parseInt(value, 10) || 0);
      if (wanted === 0) return 0;

      var line = this.lineFor(id);
      if (!line) return wanted;

      if (this.atCartLimit(line)) return 0;

      var min = line.min > 1 ? line.min : 1;
      var step = line.step > 1 ? line.step : 1;
      var room = this.ceiling(line);
      var max = room > 0 ? room : Infinity;

      if (wanted < min) wanted = min;
      if (step > 1) wanted = min + Math.round((wanted - min) / step) * step;
      if (wanted < min) wanted = min;
      if (wanted > max) wanted = min + Math.floor((max - min) / step) * step;
      return wanted;
    }

    setQty(id, next) {
      var value = this.clampQty(id, next);
      if (value > 0) this.state.qty[id] = value;
      else delete this.state.qty[id];
      this.storeState();
    }

    /** One press of - or +, honouring the variant's minimum and increment. */
    bump(id, direction) {
      var current = this.state.qty[id] || 0;
      var line = this.lineFor(id);
      var min = line && line.min > 1 ? line.min : 1;
      var step = line && line.step > 1 ? line.step : 1;

      if (direction > 0) {
        var room = this.ceiling(line);
        if (room > 0 && current >= room) return;
        this.setQty(id, current === 0 ? min : current + step);
      } else {
        this.setQty(id, current <= min ? 0 : current - step);
      }
    }

    /* -------------------------------------------------------------- *
     * Loading
     * -------------------------------------------------------------- */

    productsUrl(handle, page) {
      var root = (this.routes.root || '/').replace(/\/$/, '');
      return root + '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + (this.settings.perRequest || 250) + '&page=' + page;
    }

    fetchCollection(handle) {
      if (!this.fetchCache[handle]) {
        this.fetchCache[handle] = this.fetchCollectionUncached(handle).catch(
          function (error) {
            /* Do not cache a failure — Try again must be able to retry. */
            delete this.fetchCache[handle];
            throw error;
          }.bind(this)
        );
      }
      return this.fetchCache[handle];
    }

    async fetchCollectionUncached(handle) {
      var perRequest = this.settings.perRequest || 250;
      var maxPages = this.settings.maxPages || 4;
      var products = [];

      for (var page = 1; page <= maxPages; page++) {
        var response = await fetch(this.productsUrl(handle, page), { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Collection "' + handle + '" returned ' + response.status);
        var data = await response.json();
        var batch = data.products || [];
        products = products.concat(batch);
        if (batch.length < perRequest) return products;
      }
      /* Hit the page cap with a full last page — there is more in the collection
         than this flow is showing. Never truncate silently. */
      console.warn(
        '[colour-quick-order] "' + handle + '" has more than ' + perRequest * maxPages +
        ' products; raise maxPages or split the collection.'
      );
      this.truncated[handle] = true;
      return products;
    }

    /** Load one step for one brand (the selected one unless told otherwise), once. */
    async load(kind, forBrand) {
      var brand = forBrand || this.brand();
      if (!brand) return;
      if (!this.catalogue[brand.id]) this.catalogue[brand.id] = {};
      if (this.catalogue[brand.id][kind]) return;

      var step = (brand.steps || {})[kind] || {};
      var groups = step.products || [];
      var collections = step.collections || [];
      var key = brand.id + ':' + kind;

      /* Products are already rendered by Liquid — with this customer's catalogue
         prices, real inventory states and their B2B quantity rules. */
      var server = [];
      groups.forEach(
        function (group) {
          server = server.concat(this.mapVariantLines(brand, kind, group));
        }.bind(this)
      );

      if (!collections.length) {
        var only = this.sortForKind(this.applySplit(server, kind, brand), kind);
        this.catalogue[brand.id][kind] = only;
        this.pruneUnbuyable(only);
        return;
      }
      if (this.loading[key]) return this.loading[key];

      this.loading[key] = (async function () {
        try {
          delete this.errors[key];
          var items = server;
          var failed = [];
          /* A step may name several collections; their lines merge in order. Each
             one is fetched on its own so a single bad handle cannot take the
             healthy ones down with it — Limitless retail names two collections,
             and losing both because one was renamed is a worse failure than
             showing the half that still works. */
          for (var i = 0; i < collections.length; i++) {
            try {
              var products = await this.fetchCollection(collections[i].handle);
              items = items.concat(this.mapProducts(products, kind, brand, collections[i]));
            } catch (error) {
              failed.push(collections[i].handle);
              console.warn(
                '[colour-quick-order] ' + brand.id + ' "' + kind + '" step: ' +
                (error.message || 'could not load "' + collections[i].handle + '"')
              );
            }
          }
          items = this.sortForKind(this.applySplit(dedupeById(items), kind, brand), kind);
          /* Only block the step when nothing at all came back. A partial failure
             keeps its lines and reports the missing handle to the console, where
             a configuration mistake belongs. */
          if (failed.length && !items.length) {
            this.errors[key] = 'Collection "' + failed[0] + '" could not be loaded.';
          }
          this.catalogue[brand.id][kind] = items;
          this.pruneUnbuyable(items);
        } catch (error) {
          this.errors[key] = error.message || 'Could not load products.';
          this.catalogue[brand.id][kind] = server;
        } finally {
          delete this.loading[key];
        }
      }.bind(this))();

      return this.loading[key];
    }

    /**
     * Turn server-rendered variant records into lines, decorated for the step
     * they belong to. Same shape mapProducts produces, so every view, total and
     * cart path downstream is identical whichever source a step came from.
     */
    mapVariantLines(brand, kind, group) {
      var swatches = group.swatches || {};
      var productTitle = group.title || '';
      var lastOrder = this.lastOrder;
      var hideSoldOut = String(this.settings.inStockOnly || '').indexOf(kind) >= 0;
      var self = this;

      var out = [];
      (group.lines || []).forEach(function (variant) {
        /* Liquid money is already in cents, unlike the Ajax feed's decimals. */
        var price = parseInt(variant.price, 10) || 0;
        var compare = parseInt(variant.compare, 10) || 0;
        var available = !!variant.available && !variant.trade;
        if (hideSoldOut && !available) return;

        var multi = (group.lines || []).length > 1;
        var variantLabel = variant.title && variant.title !== 'Default Title' ? variant.title : '';

        var line = {
          id: String(variant.id),
          handle: group.handle || '',
          sku: variant.sku || '',
          price: price,
          compareAt: compare > price ? compare : 0,
          available: available,
          stock: variant.stock || 'in',
          tradeOnly: !!variant.trade,
          image: variant.image || group.image || '',
          vendor: group.vendor || brand.vendor || '',
          kind: kind,
          last: parseInt(lastOrder[String(variant.id)], 10) || 0,
          src: group.handle || '',
          srcTitle: group.title || '',
          min: parseInt(variant.min, 10) || 1,
          step: parseInt(variant.step, 10) || 1,
          max: parseInt(variant.max, 10) || 0,
          avail: parseInt(variant.avail, 10) || 0
        };

        if (kind === 'colour') {
          var shade = parseShade({ title: variant.opt, tags: [] }, {}, brand);
          /* The option may carry only the code ("6/0"), with the descriptive
             name in the variant title or a second option. Take whichever exists. */
          var name = shade.name;
          if (!name && variantLabel && variantLabel !== variant.opt) {
            name = variantLabel.split(' / ').filter(function (part) {
              return part !== variant.opt;
            }).join(' ');
          }
          line.code = shade.code;
          line.name = name || '';
          line.group = seriesKey(shade.code);
          line.sort = shadeSortValue(shade.code);
          /* An admin-configured option swatch beats the generated colour. */
          line.hex = safeColour(swatches[variant.opt], swatchForCode(shade.code));
        } else if (kind === 'dev') {
          line.vol = developerVolume({ title: productTitle }, { title: variantLabel || variant.opt }, true);
          line.size = productTitle + (variantLabel ? ' · ' + variantLabel : '');
          line.name = productTitle + (variantLabel ? ' ' + variantLabel : '');
        } else if (kind === 'bleach') {
          line.name = productTitle + (variantLabel ? ' ' + variantLabel : '');
          line.size = sizeLabel({ title: productTitle }, { title: variantLabel }, true);
          line.hex = safeColour(swatches[variant.opt], swatchForBleach(line.name));
        } else {
          line.name = productTitle + (variantLabel && multi ? ' ' + variantLabel : '');
        }

        out.push(line);
      });

      if (kind === 'colour') {
        out.sort(function (a, b) {
          var ga = SERIES_ORDER.indexOf(a.group);
          var gb = SERIES_ORDER.indexOf(b.group);
          if (ga !== gb) return (ga < 0 ? 999 : ga) - (gb < 0 ? 999 : gb);
          if (a.sort[0] !== b.sort[0]) return a.sort[0] - b.sort[0];
          return a.sort[1] - b.sort[1];
        });
      }
      return out;
    }

    /**
     * Drop restored quantities for lines that are no longer buyable, so the
     * subtotal on screen matches what the cart will actually accept.
     */
    pruneUnbuyable(items) {
      var dropped = false;
      items.forEach(
        function (item) {
          if (this.state.qty[item.id] && (item.tradeOnly || !item.available)) {
            delete this.state.qty[item.id];
            dropped = true;
          }
        }.bind(this)
      );
      if (dropped) this.storeState();
    }

    /** Turn the Ajax product feed into flat, addable lines — one per variant. */
    mapProducts(products, kind, brand, source) {
      var settings = this.settings;
      var wantedVendor = source && source.vendor ? String(source.vendor).toLowerCase() : '';
      var srcHandle = (source && source.handle) || '';
      var srcTitle = (source && source.title) || '';
      var out = [];

      products.forEach(function (product) {
        if (wantedVendor && String(product.vendor || '').toLowerCase() !== wantedVendor) return;

        var tags = product.tags || [];
        var tradeOnly = !settings.isB2b && hasTag(tags, settings.tradeRestrictedTag);
        var hideSoldOut = String(settings.inStockOnly || '').indexOf(kind) >= 0;
        var lowStock = hasTag(tags, settings.lowStockTag);
        var onOrder = hasTag(tags, settings.onOrderTag);
        var swatchTag = tagValue(tags, 'swatch:') || tagValue(tags, 'hex:');
        var variants = product.variants || [];
        var multiVariant = variants.length > 1;
        var image = (product.images && product.images[0] && product.images[0].src) || product.featured_image || '';

        variants.forEach(function (variant) {
          if (hideSoldOut && !variant.available) return;
          var price = toCents(variant.price);
          var compareAt = toCents(variant.compare_at_price);
          /* The Ajax feed only reports available/unavailable, so the middle
             states come from tags. An unavailable variant is genuinely not
             purchasable — Shopify rejects the whole /cart/add batch if one is
             in it — so those lines lose their stepper entirely. */
          var stock = !variant.available ? 'out' : lowStock ? 'low' : onOrder ? 'onorder' : 'in';

          var line = {
            id: String(variant.id),
            handle: product.handle,
            sku: variant.sku || '',
            price: price,
            compareAt: compareAt > price ? compareAt : 0,
            available: !!variant.available,
            stock: stock,
            tradeOnly: tradeOnly,
            image: (variant.featured_image && variant.featured_image.src) || image,
            vendor: product.vendor || '',
            kind: kind,
            last: parseInt(this.lastOrder[String(variant.id)], 10) || 0,
            /* Which source this came from, so a step naming several collections
               can show them as separate sections in the order they were listed. */
            src: srcHandle,
            srcTitle: srcTitle,
            /* The Ajax product feed carries no inventory count and no quantity
               rule, so these lines have no ceiling to enforce. */
            min: 1,
            step: 1,
            max: 0,
            avail: 0
          };

          if (kind === 'colour') {
            var shade = parseShade(product, variant, brand);
            line.code = shade.code;
            line.name = shade.name;
            line.group = seriesKey(shade.code);
            line.sort = shadeSortValue(shade.code);
            line.hex = safeColour(swatchTag, swatchForCode(shade.code));
          } else if (kind === 'dev') {
            line.vol = developerVolume(product, variant, multiVariant);
            line.size = product.title + (multiVariant && variant.title !== 'Default Title' ? ' · ' + variant.title : '');
            line.name = product.title + (multiVariant && variant.title !== 'Default Title' ? ' ' + variant.title : '');
          } else if (kind === 'bleach') {
            line.name = product.title + (multiVariant && variant.title !== 'Default Title' ? ' ' + variant.title : '');
            line.size = sizeLabel(product, variant, multiVariant);
            line.hex = safeColour(swatchTag, swatchForBleach(line.name));
          } else {
            line.name = product.title + (multiVariant && variant.title !== 'Default Title' ? ' ' + variant.title : '');
          }

          out.push(line);
        }, this);
      }, this);

      if (kind === 'colour') {
        out.sort(function (a, b) {
          var ga = SERIES_ORDER.indexOf(a.group);
          var gb = SERIES_ORDER.indexOf(b.group);
          if (ga !== gb) return (ga < 0 ? 999 : ga) - (gb < 0 ? 999 : gb);
          if (a.sort[0] !== b.sort[0]) return a.sort[0] - b.sort[0];
          return a.sort[1] - b.sort[1];
        });
      }
      return out;
    }

    /* -------------------------------------------------------------- *
     * Navigation
     * -------------------------------------------------------------- */

    kindsForStep(step) {
      if (step === 1) return ['colour'];
      if (step === 2) return ['dev', 'bleach'];
      if (step === 3) return RETAIL_KINDS;
      if (step === 4) return ALL_KINDS;
      return [];
    }

    async go(step) {
      var next = Math.max(0, Math.min(4, step));
      if (next > 0 && !this.state.brand) next = 0;

      /* Navigating off the confirmation ends that order: drop the confirmation
         state so the summary and footer bar come back. */
      if (this.state.done) {
        this.state.done = false;
        this.state.confirmation = null;
      }
      /* A stale cart error must not follow the shopper to the next step. */
      this.errorMessage = '';

      var changed = this.state.step !== next;
      this.state.step = next;
      this.storeState();
      this.pendingFocus = changed;

      /* Kick the fetches off BEFORE the first render. load() registers its
         in-flight promise synchronously, so isLoading() is already true by the
         time the step paints and it can show a loading state rather than
         flashing "nothing found" at a shopper waiting on a big collection. */
      var kinds = this.kindsForStep(next);
      var work = kinds.map(
        function (kind) {
          return this.load(kind);
        }.bind(this)
      );
      /* The developer step lists the other brands too, so their ranges have to
         be in hand before it renders. */
      if (next === 2) {
        this.brands.forEach(
          function (other) {
            if (other.id === this.state.brand) return;
            work.push(this.load('dev', other));
            work.push(this.load('bleach', other));
          }.bind(this)
        );
      }

      this.render();
      if (!work.length) return;

      await Promise.all(work);
      if (this.state.step === next) {
        /* The post-load render replaces the node the first render focused, so
           ask for focus again rather than dropping the shopper back on <body>. */
        this.pendingFocus = changed;
        this.render();
      }
    }

    pickBrand(id) {
      this.state.brand = id;
      this.state.group = 'all';
      this.state.q = '';
      this.state.open = {};
      this.state.pasteOpen = false;
      this.state.pasteResult = '';
      this.go(1);
    }

    /** Clear the cached failure for this step so load() will fetch again. */
    retry() {
      var brand = this.brand();
      if (!brand) return;
      this.fetchCache = {};
      this.kindsForStep(this.state.step).forEach(
        function (kind) {
          this.brands.forEach(
            function (each) {
              delete this.errors[each.id + ':' + kind];
              if (this.catalogue[each.id]) delete this.catalogue[each.id][kind];
            }.bind(this)
          );
        }.bind(this)
      );
      this.go(this.state.step);
    }

    restart() {
      this.state.step = 0;
      this.state.brand = null;
      this.state.qty = {};
      this.state.done = false;
      this.state.confirmation = null;
      this.state.openDev = {};
      this.state.openBleach = {};
      this.storeState();
      this.render();
    }

    /* -------------------------------------------------------------- *
     * Events
     * -------------------------------------------------------------- */

    handleClick(event) {
      var el = event.target.closest('[data-cqo-action], [data-cqo-pick-brand], [data-cqo-step-to]');
      if (!el || !this.contains(el)) return;

      var brandId = el.getAttribute('data-cqo-pick-brand');
      if (brandId) {
        event.preventDefault();
        this.pickBrand(brandId);
        return;
      }

      var stepTo = el.getAttribute('data-cqo-step-to');
      if (stepTo !== null) {
        event.preventDefault();
        this.go(parseInt(stepTo, 10));
        return;
      }

      var action = el.getAttribute('data-cqo-action');
      var id = el.getAttribute('data-cqo-id');

      switch (action) {
        case 'inc':
        case 'dec':
          event.preventDefault();
          this.bump(id, action === 'inc' ? 1 : -1);
          this.syncQuantities();
          break;
        case 'add6':
          event.preventDefault();
          this.setQty(id, (this.state.qty[id] || 0) + 6);
          this.syncQuantities();
          break;
        case 'view':
          event.preventDefault();
          this.state.view = el.getAttribute('data-cqo-view');
          this.storeState();
          this.render();
          break;
        case 'toggle-group':
          event.preventDefault();
          this.toggleGroup(el.getAttribute('data-cqo-group'));
          break;
        case 'toggle-brand-dev':
          event.preventDefault();
          var devKey = el.getAttribute('data-cqo-key');
          this.state.openDev[devKey] = !this.state.openDev[devKey];
          this.render();
          break;
        case 'toggle-brand-bleach':
          event.preventDefault();
          var bleachKey = el.getAttribute('data-cqo-key');
          this.state.openBleach[bleachKey] = !this.state.openBleach[bleachKey];
          this.render();
          break;
        case 'toggle-paste':
          event.preventDefault();
          this.state.pasteOpen = !this.state.pasteOpen;
          this.render();
          break;
        case 'apply-paste':
          event.preventDefault();
          this.applyPaste();
          break;
        case 'fill-last':
          event.preventDefault();
          this.fillLast();
          break;
        case 'clear-colours':
          event.preventDefault();
          this.clearColours();
          break;
        case 'back':
          event.preventDefault();
          this.go(this.state.step - 1);
          break;
        case 'next':
          event.preventDefault();
          if (this.state.step === 4) this.addAllToCart();
          else this.go(this.state.step + 1);
          break;
        case 'add-all':
          event.preventDefault();
          this.addAllToCart();
          break;
        case 'restart':
          event.preventDefault();
          this.restart();
          break;
        case 'retry':
          event.preventDefault();
          this.retry();
          break;
        default:
          break;
      }
    }

    handleImageError(event) {
      var image = event.target;
      if (!image || image.tagName !== 'IMG') return;
      var holder = image.parentElement;
      image.remove();
      if (!holder) return;
      var fallback = holder.querySelector('[data-cqo-fallback]');
      if (fallback) fallback.hidden = false;
    }

    handleInput(event) {
      var target = event.target;
      if (target.matches('[data-cqo-search]')) {
        this.onSearch(target.value);
        return;
      }
      if (target.matches('[data-cqo-paste]')) {
        this.state.paste = target.value;
        return;
      }
      if (target.matches('[data-cqo-qty]')) {
        /* Update the totals as they type, but leave the field they are in alone. */
        this.setQty(target.getAttribute('data-cqo-id'), target.value);
        var wrapper = target.closest('.cqo-qty');
        if (wrapper) wrapper.setAttribute('data-cqo-has-qty', target.value > 0 ? 'true' : 'false');
        var card = target.closest('[data-cqo-card]');
        if (card) card.setAttribute('data-picked', target.value > 0 ? 'true' : 'false');
        this.syncCounters();
      }
    }

    handleChange(event) {
      var target = event.target;
      if (target.matches('[data-cqo-group-filter]')) {
        this.state.group = target.value;
        this.renderList(false);
        return;
      }
      if (target.matches('[data-cqo-qty]')) {
        this.setQty(target.getAttribute('data-cqo-id'), target.value);
        this.syncQuantities();
      }
    }

    toggleGroup(key) {
      var groups = this.visibleGroups();
      var firstKey = groups.length ? groups[0].key : null;
      var current = this.state.open[key] !== undefined ? this.state.open[key] : key === firstKey;
      this.state.open[key] = !current;
      this.renderList(false);
    }

    /* -------------------------------------------------------------- *
     * Bulk quantity actions
     * -------------------------------------------------------------- */

    fillLast() {
      this.items('colour').forEach(
        function (item) {
          if (item.last > 0) this.setQty(item.id, item.last);
        }.bind(this)
      );
      this.renderList(false);
      this.syncQuantities();
    }

    clearColours() {
      this.items('colour').forEach(
        function (item) {
          delete this.state.qty[item.id];
        }.bind(this)
      );
      this.storeState();
      this.renderList(false);
      this.syncQuantities();
    }

    /**
     * Parse "8.1 x3" / "6.66, 2" / "10.11" — one shade per line, quantity
     * optional and defaulting to 1. Matches are additive; unknown codes are
     * reported back rather than silently dropped.
     */
    applyPaste() {
      var byCode = {};
      this.items('colour').forEach(function (item) {
        byCode[String(item.code).toLowerCase()] = item;
        if (item.sku) byCode[String(item.sku).toLowerCase()] = item;
      });

      var missed = [];
      var applied = 0;

      String(this.state.paste || '')
        .split(/[\n;]+/)
        .forEach(
          function (raw) {
            var line = raw.trim();
            if (!line) return;
            var match = line.match(/^([0-9]+(?:[./\-][0-9]+){0,2}|[A-Za-z0-9]{1,10}(?:\.[0-9]+)?)\s*(?:[x*,: ]\s*([0-9]+))?$/);
            if (!match) {
              missed.push(line);
              return;
            }
            var item = byCode[match[1].toLowerCase()];
            if (!item) {
              missed.push(match[1]);
              return;
            }
            this.setQty(item.id, (this.state.qty[item.id] || 0) + (parseInt(match[2], 10) || 1));
            applied += 1;
          }.bind(this)
        );

      this.state.pasteResult =
        applied +
        ' ' +
        plural(applied, 'line', 'lines') +
        ' applied' +
        (missed.length ? ' · not found: ' + missed.slice(0, 4).join(', ') : '');

      this.render();
    }

    /* -------------------------------------------------------------- *
     * Cart
     * -------------------------------------------------------------- */

    cartElement() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    cartSections() {
      var cart = this.cartElement();
      if (cart && typeof cart.getSectionsToRender === 'function') return cart.getSectionsToRender();
      return [{ id: 'cart-icon-bubble' }];
    }

    /** How to name a line in a message: its shade code where there is one. */
    lineLabel(line) {
      if (!line) return '';
      return line.code || line.name || line.sku || '';
    }

    /** One POST to /cart/add.js. Returns the parsed body, error payloads included. */
    async postCartAdd(items, sections) {
      var response = await fetch(this.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items: items,
          sections: sections.map(function (section) {
            return section.id;
          }),
          sections_url: window.location.pathname
        })
      });
      return response.json();
    }

    /**
     * Add the lines one at a time. /cart/add.js is atomic — one line over its
     * limit rejects the whole batch — and the 422 it returns carries nothing but
     * a message: no variant id, no title, nothing to attribute it to. So when a
     * batch fails, the lines are retried individually. The ones that can go in
     * do, and every rejection is tied to the line that actually caused it.
     */
    async addLinesIndividually(items, sections) {
      var added = [];
      var failures = [];
      var last = null;

      /* Sequential on purpose: the cart is stateful, and parallel adds race. */
      for (var i = 0; i < items.length; i++) {
        var id = String(items[i].id);
        try {
          var data = await this.postCartAdd([items[i]], sections);
          if (data.status || data.errors) {
            failures.push({ id: id, message: data.description || data.message || 'Could not be added to the cart.' });
          } else {
            added.push(id);
            last = data;
          }
        } catch (error) {
          /* The request never landed. Nothing is wrong with the line itself, so
             it keeps its quantity and can simply be sent again. */
          failures.push({ id: id, message: 'Could not be reached — try again.', transient: true });
        }
      }

      return { added: added, failures: failures, last: last };
    }

    /**
     * "6.0, 8.1 : The maximum quantity of this item is already in your cart."
     * Lines are grouped by the message they came back with, so one shared reason
     * is stated once however many shades hit it.
     */
    describeFailures(failures, index) {
      var order = [];
      var byMessage = {};

      failures.forEach(
        function (failure) {
          if (!byMessage[failure.message]) {
            byMessage[failure.message] = [];
            order.push(failure.message);
          }
          var label = this.lineLabel(index[failure.id]);
          if (label && byMessage[failure.message].indexOf(label) < 0) {
            byMessage[failure.message].push(label);
          }
        }.bind(this)
      );

      return order
        .map(function (message) {
          var labels = byMessage[message];
          if (!labels.length) return message;
          /* Shopify's own wording sometimes names the product; repeating it reads
             as a stutter. */
          var spoken = message.toLowerCase();
          labels = labels.filter(function (label) {
            return spoken.indexOf(label.toLowerCase()) < 0;
          });
          if (!labels.length) return message;
          var shown = labels.slice(0, 4).join(', ');
          if (labels.length > 4) shown += ' +' + (labels.length - 4);
          return shown + ' : ' + message;
        })
        .join(' · ');
    }

    async addAllToCart() {
      if (!Object.keys(this.state.qty).length) return;

      this.setBusy(true);
      this.setBarError('');

      /* The Add all button is reachable from steps 2-4, and a restored session
         can hold quantities for steps this brand has not fetched yet. Load them
         all before building the payload, or those lines would be dropped. */
      await Promise.all(ALL_KINDS.map(this.load.bind(this)));

      var index = this.index();
      var items = [];
      var blocked = [];
      var submitted = [];

      Object.keys(this.state.qty).forEach(function (id) {
        var line = index[id];
        if (!line || line.tradeOnly || !line.available) {
          blocked.push(id);
          return;
        }
        submitted.push(id);
        items.push({ id: parseInt(id, 10), quantity: this.state.qty[id] });
      }, this);

      if (!items.length) {
        this.setBusy(false);
        this.setBarError('Nothing in this order can be added to the cart.');
        return;
      }

      var sections = this.cartSections();

      try {
        var data = await this.postCartAdd(items, sections);
        var failures = [];

        if (data.status || data.errors) {
          if (items.length === 1) {
            /* Nothing to disentangle — the batch was the line. */
            failures = [{ id: String(items[0].id), message: data.description || data.message || 'Could not be added to the cart.' }];
            submitted = [];
            data = null;
          } else {
            /* The batch told us only that something in it was refused. Retry the
               lines separately so the good ones still go in and each rejection is
               tied to the line that caused it. */
            var retry = await this.addLinesIndividually(items, sections);
            failures = retry.failures;
            submitted = retry.added;
            data = retry.last;
          }
        }

        if (data) this.refreshCartSections(data, sections);

        /* Only the lines that actually went in are cleared; the rest stay on the
           step with their quantities, ready to be adjusted and sent again. */
        submitted.forEach(
          function (id) {
            delete this.state.qty[id];
          }.bind(this)
        );

        if (failures.length) {
          /* Whatever went in has changed the headroom on every line it touched. */
          await this.loadCartQuantities();

          /* A line the cart has refused will be refused again, so leaving it in
             the order soft-locks every later Add all behind it. Drop it, name it,
             and let the rest of the order through. A line that only failed to
             send is left alone — that one is worth retrying. */
          var dropped = failures.filter(function (failure) {
            return !failure.transient;
          });
          dropped.forEach(
            function (failure) {
              delete this.state.qty[failure.id];
            }.bind(this)
          );

          /* Say what did go in — the cart has changed, and a shopper who is only
             told what failed will not know that. */
          var note = submitted.length
            ? submitted.length + ' ' + plural(submitted.length, 'line', 'lines') + ' added. '
            : '';
          var tail = '';
          if (dropped.length === failures.length && dropped.length) {
            tail = ' ' + plural(dropped.length, 'That line has', 'Those lines have') + ' been removed from this order.';
          } else if (dropped.length) {
            /* Some failures were kept and some dropped, so "that line" would be
               ambiguous — name the ones that went. */
            var gone = [];
            dropped.forEach(
              function (failure) {
                var label = this.lineLabel(index[failure.id]);
                if (label && gone.indexOf(label) < 0) gone.push(label);
              }.bind(this)
            );
            if (gone.length) tail = ' ' + gone.slice(0, 4).join(', ') + ' removed from this order.';
          }
          this.setBarError(note + this.describeFailures(failures, index) + tail);
          this.storeState();
          this.render();
          return;
        }

        var cart = await fetch((this.routes.cart || '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (res) {
          return res.json();
        });

        this.state.confirmation = {
          count: cart.item_count,
          total: cart.total_price,
          blocked: blocked.length
        };
        this.state.done = true;
        this.state.step = 4;
        /* The order is in the cart now; a reload should start a fresh run
           rather than replay a stale confirmation. */
        this.clearStoredState();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'colour-quick-order', cartData: cart });
        }

        this.render();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        this.setBarError((window.cartStrings && window.cartStrings.error) || 'The cart could not be reached. Please try again.');
      } finally {
        this.setBusy(false);
      }
    }

    /** Swap the theme's own cart sections in place, without opening the drawer. */
    refreshCartSections(data, sections) {
      if (!data.sections) return;
      var parser = new DOMParser();

      sections.forEach(function (section) {
        var html = data.sections[section.id];
        if (!html) return;
        var target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (!target) return;
        var parsed = parser.parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
        if (parsed) target.innerHTML = parsed.innerHTML;
      });

      var cart = this.cartElement();
      if (cart && cart.tagName === 'CART-DRAWER') {
        cart.classList.remove('is-empty');
        var inner = cart.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        var overlay = cart.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof cart.close === 'function') overlay.addEventListener('click', cart.close.bind(cart));
      }
    }

    setBusy(busy) {
      this.toggleAttribute('data-cqo-busy', busy);
      this.querySelectorAll('[data-cqo-action="add-all"], [data-cqo-action="next"]').forEach(function (button) {
        button.disabled = busy;
      });
    }

    setBarError(message) {
      this.errorMessage = message;
      var slot = this.querySelector('[data-cqo-error]');
      if (slot) {
        slot.textContent = message || '';
        slot.hidden = !message;
      }
    }

    /* -------------------------------------------------------------- *
     * Derived view data
     * -------------------------------------------------------------- */

    matchedShades() {
      var query = String(this.state.q || '').trim().toLowerCase();
      var group = this.state.group;

      return this.items('colour').filter(function (item) {
        if (group !== 'all' && item.group !== group) return false;
        if (!query) return true;
        return (item.code + ' ' + item.name + ' ' + item.sku).toLowerCase().indexOf(query) >= 0;
      });
    }

    visibleGroups() {
      var buckets = {};
      this.matchedShades().forEach(function (item) {
        (buckets[item.group] = buckets[item.group] || []).push(item);
      });

      return Object.keys(buckets)
        .sort(function (a, b) {
          var ia = SERIES_ORDER.indexOf(a);
          var ib = SERIES_ORDER.indexOf(b);
          return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
        })
        .map(function (key) {
          return { key: key, label: SERIES_LABEL[key] || 'Series ' + key, items: buckets[key] };
        });
    }

    isLoading(kinds) {
      var brand = this.brand();
      if (!brand) return false;
      return kinds.some(
        function (kind) {
          return !!this.loading[brand.id + ':' + kind];
        }.bind(this)
      );
    }

    errorFor(kinds) {
      var brand = this.brand();
      if (!brand) return '';
      for (var i = 0; i < kinds.length; i++) {
        var message = this.errors[brand.id + ':' + kinds[i]];
        if (message) return message;
      }
      return '';
    }

    /* -------------------------------------------------------------- *
     * Rendering
     * -------------------------------------------------------------- */

    render() {
      var step = this.state.step;
      var showChrome = step > 0 && !this.state.done;

      this.rail.innerHTML = this.railHTML();

      if (step === 0) {
        this.main.innerHTML = this.brandStepHTML;
        this.paintBrandChips();
      } else if (step === 1) {
        this.main.innerHTML = this.colourStepHTML();
      } else if (step === 2) {
        this.main.innerHTML = this.developerStepHTML();
      } else if (step === 3) {
        this.main.innerHTML = this.retailStepHTML();
      } else {
        this.main.innerHTML = this.state.done ? this.confirmationHTML() : this.reviewStepHTML();
      }

      this.summary.hidden = !showChrome;
      this.summary.innerHTML = showChrome ? this.summaryHTML() : '';

      this.bar.hidden = !showChrome;
      this.bar.innerHTML = showChrome ? this.barHTML() : '';

      if (this.errorMessage) this.setBarError(this.errorMessage);
      if (this.pendingFocus) {
        this.pendingFocus = false;
        this.focusStep();
      }
    }

    /** Put focus on the heading of the step that just replaced the content. */
    focusStep() {
      var heading = this.main.querySelector('.cqo-h1, .cqo-done__metric');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }

    /** Re-render only the shade list — used while typing, so focus is restored. */
    renderList(keepFocus) {
      if (this.state.step !== 1) return this.render();

      var slot = this.querySelector('[data-cqo-list]');
      if (!slot) return this.render();

      slot.innerHTML = this.listHTML();

      var counters = this.querySelector('[data-cqo-counters]');
      if (counters) counters.innerHTML = this.countersHTML();

      if (keepFocus) {
        var search = this.querySelector('[data-cqo-search]');
        if (search && document.activeElement !== search) {
          var value = search.value;
          search.focus();
          search.setSelectionRange(value.length, value.length);
        }
      }
      this.syncSummary();
    }

    railHTML() {
      var step = this.state.step;
      var unlocked = !!this.state.brand;

      return STEP_LABELS.map(
        function (label, i) {
          var state = i === step ? 'current' : i < step ? 'done' : 'todo';
          var clickable = i === 0 || unlocked;
          return (
            '<button type="button" class="cqo-rail__tab" data-state="' + state + '"' +
            (clickable ? ' data-cqo-step-to="' + i + '"' : ' disabled') +
            (i === step ? ' aria-current="step"' : '') +
            '>' +
            '<span class="cqo-rail__n">' + (i + 1) + '</span>' +
            '<span class="cqo-rail__label">' + escapeHtml(label) + '</span>' +
            '</button>'
          );
        }
      ).join('');
    }

    /** Fills the seven palette chips on each server-rendered brand tile. */
    paintBrandChips() {
      this.brands.forEach(
        function (brand) {
          var slot = this.querySelector('[data-cqo-chips="' + brand.id + '"]');
          if (!slot) return;

          /* A product-sourced brand already knows its real swatch colours. */
          var colourGroups = ((brand.steps || {}).colour || {}).products || [];
          if (colourGroups.length) {
            var shades = this.mapVariantLines(brand, 'colour', colourGroups[0]);
            if (shades.length) {
              var stride = Math.max(1, Math.floor(shades.length / 7));
              var picked = [];
              for (var j = 0; j < shades.length && picked.length < 7; j += stride) picked.push(shades[j]);
              slot.innerHTML = picked
                .map(function (shade) {
                  return '<span class="swatch swatch--square cqo-chip" style="--swatch--background: ' + swatchBackground(shade) + ';"></span>';
                })
                .join('');
              return;
            }
          }

          var titles = brand.chipTitles || [];
          var codes = [];
          titles.forEach(
            function (title) {
              var shade = parseShade({ title: title, tags: [] }, {}, brand);
              if (shade.code) codes.push(shade.code);
            }
          );
          if (!codes.length) return;

          var step = Math.max(1, Math.floor(codes.length / 7));
          var picked = [];
          for (var i = 0; i < codes.length && picked.length < 7; i += step) picked.push(codes[i]);

          slot.innerHTML = picked
            .map(function (code) {
              return '<span class="swatch swatch--square cqo-chip" style="--swatch--background: ' + swatchForCode(code) + ';"></span>';
            })
            .join('');
        }.bind(this)
      );
    }

    stepHeadHTML(step, title, extra) {
      var brand = this.brand();
      return (
        '<div class="cqo-stephead">' +
        '<div class="cqo-stephead__text">' +
        '<span class="cqo-eyebrow">Step ' + (step + 1) + (brand ? ': ' + escapeHtml(brand.name) : '') + '</span>' +
        '<h1 class="cqo-h1 cqo-h1--step">' + escapeHtml(title) + '</h1>' +
        '</div>' +
        (extra || '') +
        '</div>'
      );
    }

    /** Roughly how many lines a step is about to show, for the skeleton. */
    expectedCount(kinds) {
      var brand = this.brand();
      if (!brand) return 8;
      var total = 0;
      kinds.forEach(function (kind) {
        var step = (brand.steps || {})[kind] || {};
        (step.products || []).forEach(function (group) {
          total += (group.lines || []).length;
        });
        (step.collections || []).forEach(function (source) {
          total += source.count || 0;
        });
      });
      return Math.max(4, Math.min(total || 8, 18));
    }

    /**
     * A skeleton rather than a bare spinner: a big range takes a moment to come
     * back, and reserving the height stops the page jumping when it lands.
     */
    loadingHTML(kinds, gridClass) {
      var count = this.expectedCount(kinds);
      var cards = '';
      for (var i = 0; i < count; i++) cards += '<div class="cqo-skeleton" aria-hidden="true"></div>';

      return (
        '<div class="cqo-loading" role="status" aria-live="polite">' +
        '<p class="cqo-loading__msg">' +
        '<span class="cqo-spinner" aria-hidden="true"></span>' +
        'Loading ' + count + '+ products…' +
        '</p>' +
        '<div class="cqo-grid ' + (gridClass || 'cqo-grid--swatch') + '">' + cards + '</div>' +
        '</div>'
      );
    }

    stateHTML(kinds, emptyMessage, gridClass) {
      if (this.isLoading(kinds)) {
        return this.loadingHTML(kinds, gridClass);
      }
      var error = this.errorFor(kinds);
      if (error) {
        return (
          '<div class="cqo-state cqo-state--error" role="alert">' +
          '<p>' + escapeHtml(error) + '</p>' +
          '<button type="button" class="cqo-btn cqo-btn--outline" data-cqo-action="retry">Try again</button>' +
          '</div>'
        );
      }
      if (emptyMessage) {
        return '<div class="cqo-state"><p>' + escapeHtml(emptyMessage) + '</p></div>';
      }
      return '';
    }

    /* ---- Step 2: colours ---- */

    colourStepHTML() {
      var brand = this.brand();
      if (!brand) return '';

      var actions =
        '<div class="cqo-stephead__actions">' +
        '<button type="button" class="cqo-btn cqo-btn--outline" data-cqo-action="toggle-paste" aria-expanded="' + (this.state.pasteOpen ? 'true' : 'false') + '">Paste a list</button>' +
        (this.settings.showLastOrdered && Object.keys(this.lastOrder).length
          ? '<button type="button" class="cqo-btn cqo-btn--outline" data-cqo-action="fill-last">Fill last order qty</button>'
          : '') +
        '<button type="button" class="cqo-btn cqo-btn--link" data-cqo-action="clear-colours">Clear</button>' +
        '</div>';

      var head =
        '<div class="cqo-stephead">' +
        '<div class="cqo-stephead__text">' +
        '<span class="cqo-eyebrow">Step 2' + (brand ? ': ' + escapeHtml(brand.name) : '') + '</span>' +
        '<h1 class="cqo-h1 cqo-h1--step">Choose shades</h1>' +
        '</div>' +
        actions +
        '</div>';

      var body = this.stateHTML(['colour'], this.items('colour').length ? '' : 'No shades found in this brand’s colour collection.', 'cqo-grid--swatch');

      return (
        '<section class="cqo-step">' +
        head +
        (this.state.pasteOpen ? this.pasteHTML() : '') +
        this.filtersHTML() +
        '<div class="cqo-counters" data-cqo-counters>' + this.countersHTML() + '</div>' +
        '<div data-cqo-list>' + (body || this.listHTML()) + '</div>' +
        '</section>'
      );
    }

    pasteHTML() {
      return (
        '<div class="cqo-paste">' +
        '<div class="cqo-paste__field">' +
        '<span class="cqo-label cqo-label--sm">Paste shade codes from your stocktake</span>' +
        '<textarea class="cqo-textarea" rows="4" data-cqo-paste placeholder="8.1 x3&#10;6.66 x2&#10;10.11, 4">' + escapeHtml(this.state.paste) + '</textarea>' +
        '<span class="cqo-hint">One per line — code then quantity. Unknown codes are listed back to you.</span>' +
        '</div>' +
        '<div class="cqo-paste__side">' +
        '<button type="button" class="cqo-btn cqo-btn--invert" data-cqo-action="apply-paste">Apply to list</button>' +
        '<span class="cqo-hint" aria-live="polite">' + escapeHtml(this.state.pasteResult || 'Nothing applied yet.') + '</span>' +
        '</div>' +
        '</div>'
      );
    }

    filtersHTML() {
      var shades = this.items('colour');
      var present = {};
      shades.forEach(function (item) {
        present[item.group] = (present[item.group] || 0) + 1;
      });

      var options = ['<option value="all">All series (' + shades.length + ')</option>'].concat(
        SERIES_ORDER.filter(function (key) {
          return present[key];
        }).map(
          function (key) {
            var label = (SERIES_LABEL[key] || 'Series ' + key) + ' (' + present[key] + ')';
            return '<option value="' + escapeHtml(key) + '"' + (this.state.group === key ? ' selected' : '') + '>' + escapeHtml(label) + '</option>';
          }.bind(this)
        )
      );

      var views = VIEW_OPTIONS.map(
        function (option) {
          var active = this.state.view === option[0];
          return (
            '<button type="button" class="cqo-seg__btn" data-cqo-action="view" data-cqo-view="' + option[0] + '"' +
            (active ? ' data-active="true" aria-pressed="true"' : ' aria-pressed="false"') +
            '>' + option[1] + '</button>'
          );
        }.bind(this)
      ).join('');

      return (
        '<div class="cqo-filters">' +
        '<input type="search" class="cqo-input" data-cqo-search value="' + escapeHtml(this.state.q) + '" placeholder="Search shade code or name — try 8.1 or ash" aria-label="Search shades">' +
        '<select class="cqo-select" data-cqo-group-filter aria-label="Filter by series">' + options.join('') + '</select>' +
        '<div class="cqo-seg" role="group" aria-label="List view">' + views + '</div>' +
        '</div>'
      );
    }

    countersHTML() {
      var shown = this.matchedShades().length;
      return (
        '<span><b>' + shown + '</b> shades shown</span>' +
        '<span><b data-cqo-colour-lines>' + this.lines(['colour']).length + '</b> selected</span>' +
        '<span><b data-cqo-colour-units>' + this.units(['colour']) + '</b> tubes</span>' +
        '<span><b class="cqo-red" data-cqo-colour-total>' + this.money(this.sum(['colour'])) + '</b> colour subtotal</span>'
      );
    }

    listHTML() {
      if (!this.items('colour').length) return this.stateHTML(['colour'], 'No shades found in this brand’s colour collection.', 'cqo-grid--swatch');

      var groups = this.visibleGroups();
      if (!groups.length) return '<div class="cqo-state"><p>No shades match that search.</p></div>';

      if (this.state.view === 'rows') {
        return '<div class="cqo-dense">' + this.matchedShades().map(this.denseRowHTML.bind(this)).join('') + '</div>';
      }

      if (this.state.view === 'acc') {
        var firstKey = groups[0].key;
        return (
          '<div class="cqo-acc">' +
          groups
            .map(
              function (group) {
                var open = this.state.open[group.key] !== undefined ? this.state.open[group.key] : group.key === firstKey;
                var picked = group.items.reduce(
                  function (total, item) {
                    return total + (this.state.qty[item.id] || 0);
                  }.bind(this),
                  0
                );
                return (
                  '<div class="cqo-acc__panel">' +
                  '<button type="button" class="cqo-acc__head" data-cqo-action="toggle-group" data-cqo-group="' + escapeHtml(group.key) + '" aria-expanded="' + (open ? 'true' : 'false') + '">' +
                  '<span class="cqo-label cqo-label--sm">' + escapeHtml(group.label) + '</span>' +
                  '<span class="cqo-acc__count">' + group.items.length + ' shades</span>' +
                  '<span class="cqo-acc__spacer"></span>' +
                  '<span class="cqo-acc__chips">' +
                  group.items
                    .slice(0, 5)
                    .map(function (item) {
                      return '<span class="swatch swatch--square cqo-chip" style="--swatch--background: ' + swatchBackground(item) + ';"></span>';
                    })
                    .join('') +
                  '</span>' +
                  '<span class="cqo-acc__tally cqo-red" data-cqo-group-tally="' + escapeHtml(group.key) + '">' + (picked ? picked + ' ' + plural(picked, 'tube', 'tubes') : '') + '</span>' +
                  '<span class="cqo-acc__caret" aria-hidden="true">' + (open ? '▴' : '▾') + '</span>' +
                  '</button>' +
                  (open ? '<div class="cqo-acc__body">' + group.items.map(this.denseRowHTML.bind(this)).join('') + '</div>' : '') +
                  '</div>'
                );
              }.bind(this)
            )
            .join('') +
          '</div>'
        );
      }

      return groups
        .map(
          function (group) {
            return (
              '<div class="cqo-group">' +
              '<div class="cqo-group__head">' +
              '<span class="cqo-label cqo-label--sm">' + escapeHtml(group.label) + '</span>' +
              '<span class="cqo-group__count">' + group.items.length + ' shades</span>' +
              '</div>' +
              '<div class="cqo-grid cqo-grid--swatch">' + group.items.map(this.swatchCardHTML.bind(this)).join('') + '</div>' +
              '</div>'
            );
          }.bind(this)
        )
        .join('');
    }

    stockLabel(item) {
      if (item.stock === 'in') return 'In stock';
      if (item.stock === 'low') return 'Low stock';
      if (item.stock === 'onorder') return 'On order';
      return 'Sold out';
    }

    /** Trade-restricted lines carry exactly one "Trade only" message, on the
     *  stepper, so the price and the stock badge are both suppressed. */
    stockHTML(item) {
      if (item.tradeOnly) return '';
      return '<span class="cqo-stock" data-stock="' + item.stock + '">' + this.stockLabel(item) + '</span>';
    }

    priceHTML(item, extraClass) {
      if (item.tradeOnly) return '';
      return '<span class="cqo-price' + (extraClass ? ' ' + extraClass : '') + '">' + this.money(item.price) + '</span>';
    }

    /** "+6 pack" — offered wherever there is room for it. */
    addSixHTML(item) {
      if (item.tradeOnly || !item.available) return '';
      if (this.atCartLimit(item)) return '';
      return '<button type="button" class="cqo-btn cqo-btn--outline cqo-btn--sm" data-cqo-action="add6" data-cqo-id="' + item.id + '">+6 pack</button>';
    }

    stepperHTML(item, size) {
      var qty = this.state.qty[item.id] || 0;
      if (item.tradeOnly) return '<span class="cqo-trade-lock">Trade only</span>';
      if (!item.available) return '<span class="cqo-trade-lock cqo-trade-lock--muted">Sold out</span>';

      /* Everything already in the cart counts against the ceiling, so a shopper
         is stopped here rather than by a 422 after they press Add all. */
      /* A line already in the order stays adjustable even when the cart holds
         the cap — otherwise the review offers no way to take it back out. */
      if (this.atCartLimit(item) && !qty) {
        return '<span class="cqo-trade-lock cqo-trade-lock--muted">Max in cart</span>';
      }

      var label = escapeHtml(item.code || item.name);
      var step = item.step > 1 ? item.step : 1;
      var room = this.ceiling(item);
      /* The ceiling is enforced — the input carries it, + stops at it, and
         clampQty holds the line — but it is not advertised. "maximum 40" on a
         well-stocked shade is noise; the shopper only meets the limit if they
         actually reach for it. It stays on the aria-label, which is not shown
         but is the only way a screen reader learns why + went quiet. */
      var rule = [];
      if (item.min > 1) rule.push('minimum ' + item.min);
      if (step > 1) rule.push('in ' + step + 's');
      var spoken = rule.slice();
      if (room > 0) spoken.push('maximum ' + room);
      var ruleText = spoken.length ? ' (' + spoken.join(', ') + ')' : '';
      var atCeiling = room > 0 && qty >= room;

      return (
        '<div class="cqo-qty" data-size="' + (size || 'lg') + '" data-cqo-has-qty="' + (qty > 0 ? 'true' : 'false') + '" data-cqo-row="' + item.id + '">' +
        '<button type="button" class="cqo-qty__btn" data-cqo-action="dec" data-cqo-id="' + item.id + '" aria-label="Decrease quantity of ' + label + '">−</button>' +
        '<input class="cqo-qty__input" type="number" inputmode="numeric" min="0" step="' + step + '"' + (room > 0 ? ' max="' + room + '"' : '') + ' value="' + qty + '" data-cqo-qty data-cqo-id="' + item.id + '" aria-label="Quantity of ' + label + ruleText + '">' +
        '<button type="button" class="cqo-qty__btn" data-cqo-action="inc" data-cqo-id="' + item.id + '"' + (atCeiling ? ' disabled aria-disabled="true"' : '') + ' aria-label="Increase quantity of ' + label + '">+</button>' +
        '</div>' +
        /* Dense and accordion rows have no room for the hint; the rule is still
           enforced there and is on the input's aria-label either way. */
        (rule.length && size !== 'sm' ? '<span class="cqo-last cqo-rule">' + escapeHtml(rule.join(' · ')) + '</span>' : '')
      );
    }

    swatchCardHTML(item) {
      var meta = this.priceHTML(item) + this.stockHTML(item);
      return (
        '<div class="cqo-card">' +
        '<div class="cqo-card__top">' +
        '<span class="swatch swatch--square" style="--swatch--background: ' + swatchBackground(item) + ';"></span>' +
        '<span class="cqo-card__id">' +
        '<code class="cqo-code">' + escapeHtml(item.code) + '</code>' +
        (item.name ? '<span class="cqo-card__name">' + escapeHtml(item.name) + '</span>' : '') +
        '</span>' +
        '</div>' +
        (meta ? '<div class="cqo-card__row">' + meta + '</div>' : '') +
        '<div class="cqo-card__row cqo-card__row--actions">' +
        this.stepperHTML(item, 'lg') +
        this.addSixHTML(item) +
        '</div>' +
        '</div>'
      );
    }

    denseRowHTML(item) {
      return (
        '<div class="cqo-row">' +
        '<span class="swatch swatch--square cqo-swatch--sm" style="--swatch--background: ' + swatchBackground(item) + ';"></span>' +
        '<code class="cqo-code cqo-row__code">' + escapeHtml(item.code) + '</code>' +
        '<span class="cqo-row__name">' + escapeHtml(item.name) + '</span>' +
        '<span class="cqo-row__price">' + (item.tradeOnly ? '' : this.money(item.price)) + '</span>' +
        '<span class="cqo-dot" data-stock="' + (item.tradeOnly ? 'trade' : item.stock) + '" title="' + (item.tradeOnly ? 'Trade only' : this.stockLabel(item)) + '"></span>' +
        this.stepperHTML(item, 'sm') +
        '</div>'
      );
    }

    /* ---- Step 3: developer & bleach ---- */

    developerStepHTML() {
      var brand = this.brand();
      /* Settled once per render, so the top section and the panels below agree on
         who owns a shared developer. */
      var owner = this.devOwnership();
      var dev = this.ownDevItems(owner);
      var bleach = this.items('bleach');
      var pending = this.stateHTML(['dev', 'bleach'], '', 'cqo-grid--dev');

      var who = brand ? escapeHtml(brand.name) : 'This brand';

      var devBody = dev.length
        ? '<div class="cqo-grid cqo-grid--dev">' + dev.map(this.devCardHTML.bind(this)).join('') + '</div>'
        : pending || '<div class="cqo-state"><p>' + who + ' developers are not set up yet — pick one from another brand below, or add them to your order separately.</p></div>';

      devBody += this.otherBrandDevHTML(owner);

      var groups = this.bleachGroups();
      var bleachBody;
      if (!bleach.length && !groups.ours.length && !groups.others.length) {
        bleachBody = pending || '<div class="cqo-state"><p>No bleach or lightener is linked to ' + who + ' yet.</p></div>';
      } else {
        bleachBody = groups.ours.length
          ? '<div class="cqo-grid cqo-grid--dev">' + groups.ours.map(this.bleachCardHTML.bind(this)).join('') + '</div>'
          : '<div class="cqo-state"><p>' + who + ' has no lightener of its own — pick one from another brand below.</p></div>';

        if (groups.others.length) {
          var panels = groups.others
            .map(
              function (group) {
                return this.brandPanelHTML({
                  key: group.label,
                  label: group.label,
                  items: group.items,
                  open: !!this.state.openBleach[group.label],
                  action: 'toggle-brand-bleach',
                  card: this.bleachCardHTML.bind(this)
                });
              }.bind(this)
            )
            .join('');

          bleachBody +=
            '<div class="cqo-sechead cqo-sechead--rule cqo-sechead--spaced">' +
            '<h3 class="cqo-label cqo-label--sm">Other brand lighteners</h3>' +
            '</div>' +
            '<div class="cqo-acc">' + panels + '</div>';
        }
      }

      /* Lighteners is a second heading of equal weight, not a sub-head. */
      return (
        '<section class="cqo-step">' +
        this.stepHeadHTML(2, 'Developers') +
        devBody +
        '<h2 class="cqo-h1 cqo-h1--step cqo-h1--section">Lighteners</h2>' +
        bleachBody +
        '</section>'
      );
    }

    /** A collapsed "other brand" panel, used by both developers and bleach. */
    brandPanelHTML(options) {
      var picked = options.items.reduce(
        function (total, item) {
          return total + (this.state.qty[item.id] || 0);
        }.bind(this),
        0
      );

      return (
        '<div class="cqo-acc__panel">' +
        '<button type="button" class="cqo-acc__head" data-cqo-action="' + options.action + '" data-cqo-key="' + escapeHtml(options.key) + '" aria-expanded="' + (options.open ? 'true' : 'false') + '">' +
        '<span class="cqo-label cqo-label--sm">' + escapeHtml(options.label) + '</span>' +
        '<span class="cqo-acc__count">' + (options.note && !options.items.length ? 'unavailable' : '') + '</span>' +
        '<span class="cqo-acc__spacer"></span>' +
        '<span class="cqo-acc__tally cqo-red" data-cqo-panel-tally="' + escapeHtml(options.action + ':' + options.key) + '">' + (picked ? picked + ' picked' : '') + '</span>' +
        '<span class="cqo-acc__caret" aria-hidden="true">' + (options.open ? '▴' : '▾') + '</span>' +
        '</button>' +
        (options.open
          ? '<div class="cqo-acc__body cqo-acc__body--cards">' +
            (options.items.length
              ? '<div class="cqo-grid cqo-grid--dev">' + options.items.map(options.card).join('') + '</div>'
              : '<div class="cqo-state"><p>' + escapeHtml(options.note || ('Nothing loaded for ' + options.label + '.')) + '</p></div>') +
            '</div>'
          : '') +
        '</div>'
      );
    }

    /** Sources a step names, products and collections alike, as plain handles. */
    sourceHandles(brand, kind) {
      var step = (brand.steps || {})[kind] || {};
      return (step.products || [])
        .map(function (group) {
          return group.handle;
        })
        .concat(
          (step.collections || []).map(function (source) {
            return source.handle;
          })
        )
        .sort()
        .join('|');
    }

    /** Developer lines come back in collection order; put them in reading order. */
    sortForKind(lines, kind) {
      if (kind !== 'dev') return lines;
      return lines.slice().sort(function (a, b) {
        var va = developerSortValue(a);
        var vb = developerSortValue(b);
        if (va[0] !== vb[0]) return va[0] - vb[0];
        if (va[1] !== vb[1]) return va[1] - vb[1];
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    }

    /** Only split when developer and bleach really do share one source. */
    applySplit(lines, kind, brand) {
      if (kind !== 'dev' && kind !== 'bleach') return lines;
      if (this.sourceHandles(brand, 'dev') !== this.sourceHandles(brand, 'bleach')) return lines;
      return splitDevBleach(lines, kind, {
        dev: this.settings.devPattern || 'developer|peroxide|oxide|activator|\\d+\\s*vol',
        bleach: this.settings.bleachPattern || 'bleach|lighten|blondor|plex|decolour|decolor|dust.?free'
      });
    }

    /** True when another brand draws its bleach from the same collection. */
    sharesBleach(brand) {
      var mine = ((brand.steps || {}).bleach || {}).collections || [];
      if (!mine.length) return false;
      var handles = mine.map(function (source) {
        return source.handle;
      });
      return this.brands.some(function (other) {
        if (other.id === brand.id) return false;
        return (((other.steps || {}).bleach || {}).collections || []).some(function (source) {
          return handles.indexOf(source.handle) >= 0;
        });
      });
    }

    /**
     * Every variant id a developer step lays claim to, anywhere in the table. A
     * product filed under developers is a developer wherever else it is also
     * filed, and the developer column is named before the bleach column, so it
     * keeps the line and the bleach step gives it up. This is what keeps Wella's
     * peroxide out of the bleach step now that its developers have their own
     * collection but its bleach still comes from the wider vendor collection that
     * carries both.
     */
    devClaimedIds() {
      var ids = {};
      Object.keys(this.catalogue).forEach(
        function (brandId) {
          this.itemsFor(brandId, 'dev').forEach(function (item) {
            ids[item.id] = true;
          });
        }.bind(this)
      );
      return ids;
    }

    /**
     * Every bleach line this session has loaded, deduplicated and with anything a
     * developer step already claims removed. Most brands point at the one bleach
     * collection, so this is normally a single fetch — but it also copes with a
     * brand that has its own range, or one whose range overlaps another's.
     */
    bleachPool() {
      var claimed = this.devClaimedIds();
      var seen = {};
      var pool = [];
      Object.keys(this.catalogue).forEach(
        function (brandId) {
          this.itemsFor(brandId, 'bleach').forEach(function (item) {
            if (!seen[item.id] && !claimed[item.id]) {
              seen[item.id] = true;
              pool.push(item);
            }
          });
        }.bind(this)
      );
      return pool;
    }

    /**
     * Bleach split by vendor: the selected brand's own lightener first and open,
     * everything else collapsed beneath it — the same shape as the developer step,
     * except the grouping is by vendor because one collection serves every brand.
     */
    bleachGroups() {
      var brand = this.brand();
      var ours = [];
      var byVendor = {};
      var order = [];

      /* A brand with its own lightener collection — rather than a share of the
         one everybody uses — owns everything in it, whatever the vendor says. */
      var ownIds = {};
      if (brand && !this.sharesBleach(brand)) {
        this.itemsFor(brand.id, 'bleach').forEach(function (item) {
          ownIds[item.id] = true;
        });
      }

      this.bleachPool().forEach(function (item) {
        if (ownIds[item.id] || (brand && vendorMatches(item.vendor, brand.vendor))) {
          ours.push(item);
          return;
        }
        var label = item.vendor || 'Other lighteners';
        if (!byVendor[label]) {
          byVendor[label] = [];
          order.push(label);
        }
        byVendor[label].push(item);
      });

      return {
        ours: ours,
        others: order.map(function (label) {
          return { label: label, items: byVendor[label] };
        })
      };
    }

    /**
     * Developers from the other brands, each in its own collapsed panel under the
     * selected brand's. Salons mix brands here, and the alternative is making them
     * restart the whole flow to reach another brand's peroxide.
     */
    /**
     * Which developer source owns which lines. Collections overlap — the catch-all
     * `loreal-peroxides-developers` carries Wella's Welloxon as well as L'Oreal's
     * own — and a developer should be offered once, not once per brand that files
     * it. The first source listed in the brand table keeps the line, so ownership
     * is settled by table order and does not shift with whichever brand the shopper
     * happens to have selected. Keyed by source rather than by brand, because the
     * four Wella ranges share one collection and must all see the same set.
     */
    devOwnership() {
      var owner = {};
      var claimed = {};
      this.brands.forEach(
        function (brand) {
          var signature = this.sourceHandles(brand, 'dev');
          if (!signature || owner[signature]) return;
          owner[signature] = this.itemsFor(brand.id, 'dev').filter(function (item) {
            if (claimed[item.id]) return false;
            claimed[item.id] = true;
            return true;
          });
        }.bind(this)
      );
      return owner;
    }

    /** The selected brand's developers, minus anything an earlier source claimed. */
    ownDevItems(owner) {
      var brand = this.brand();
      if (!brand) return [];
      var signature = this.sourceHandles(brand, 'dev');
      if (!signature) return [];
      return (owner || this.devOwnership())[signature] || [];
    }

    /** The other brands' developer panels, each line appearing in only one of them. */
    devPanelGroups(owner) {
      var ownership = owner || this.devOwnership();
      return this.otherDevBrands()
        .map(
          function (other) {
            return { brand: other, items: ownership[this.sourceHandles(other, 'dev')] || [] };
          }.bind(this)
        )
        .filter(
          function (group) {
            if (group.items.length) return true;
            /* A panel is dropped only when its range is in hand and every line
               turned out to be a duplicate. One still loading, or one whose
               collection failed to load, keeps its place — vanishing silently
               would hide a broken handle instead of reporting it. */
            var key = group.brand.id + ':dev';
            if (this.errors[key] || this.loading[key]) return true;
            var loaded = this.catalogue[group.brand.id] && this.catalogue[group.brand.id].dev;
            return !loaded;
          }.bind(this)
        );
    }

    otherBrandDevHTML(owner) {
      var others = this.devPanelGroups(owner);
      if (!others.length) return '';

      var panels = others
        .map(
          function (group) {
            return this.brandPanelHTML({
              key: group.brand.id,
              label: group.brand.name + ' developers',
              items: group.items,
              open: !!this.state.openDev[group.brand.id],
              action: 'toggle-brand-dev',
              card: this.devCardHTML.bind(this),
              note: this.errors[group.brand.id + ':dev'] || ''
            });
          }.bind(this)
        )
        .join('');

      return (
        '<div class="cqo-sechead cqo-sechead--rule cqo-sechead--spaced">' +
        '<h3 class="cqo-label cqo-label--sm">Other brand developers</h3>' +
        '</div>' +
        '<div class="cqo-acc">' + panels + '</div>'
      );
    }

    /** The full product name carries the volume, so neither is repeated. */
    devCardHTML(item) {
      var qty = this.state.qty[item.id] || 0;
      return (
        '<div class="cqo-card cqo-card--dev" data-cqo-card="' + item.id + '" data-picked="' + (qty > 0 ? 'true' : 'false') + '">' +
        '<span class="cqo-card__name cqo-card__name--strong">' + escapeHtml(item.name) + '</span>' +
        '<span class="cqo-card__spacer"></span>' +
        this.priceHTML(item) +
        '<span class="cqo-card__row cqo-card__row--actions">' +
        this.stepperHTML(item, 'lg') +
        this.addSixHTML(item) +
        '</span>' +
        '</div>'
      );
    }

    /**
     * Lighteners read exactly like developers. The generated colour block it used
     * to carry implied a shade the product does not have; set bleach_media to
     * 'image' to show the real product shot instead.
     */
    bleachCardHTML(item) {
      var qty = this.state.qty[item.id] || 0;
      var media =
        this.settings.bleachMedia === 'image' && item.image
          ? '<span class="cqo-card__shot">' +
            '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.name) + '" loading="lazy" width="300" height="300">' +
            '</span>'
          : '';

      return (
        '<div class="cqo-card cqo-card--dev" data-cqo-card="' + item.id + '" data-picked="' + (qty > 0 ? 'true' : 'false') + '">' +
        media +
        '<span class="cqo-card__name cqo-card__name--strong">' + escapeHtml(item.name) + '</span>' +
        '<span class="cqo-card__spacer"></span>' +
        this.priceHTML(item) +
        '<span class="cqo-card__row cqo-card__row--actions">' +
        this.stepperHTML(item, 'lg') +
        this.addSixHTML(item) +
        '</span>' +
        '</div>'
      );
    }

    /* ---- Step 4: retail ---- */

    /**
     * Lines split by the collection they came from, in the order the brand table
     * named them — so a step can be arranged as Haircare, Styling, Scalp Rescue
     * rather than one undifferentiated wall.
     */
    sourceGroups(items) {
      var groups = [];
      var byKey = {};
      items.forEach(function (item) {
        var key = item.src || '';
        if (!byKey[key]) {
          byKey[key] = { title: item.srcTitle || '', items: [] };
          groups.push(byKey[key]);
        }
        byKey[key].items.push(item);
      });
      return groups;
    }

    /** One section of step 4. Charts and accessories stay invisible until stocked. */
    retailSectionHTML(kind, heading, required) {
      var items = this.items(kind);
      if (!items.length) {
        if (!required) return '';
        var brand = this.brand();
        return (
          (heading ? this.retailHeadHTML(heading) : '') +
          this.stateHTML([kind], 'No retail range is linked to ' + (brand ? brand.name : 'this brand') + ' yet.', 'cqo-grid--retail')
        );
      }

      var groups = this.sourceGroups(items);
      /* A single source needs no label — the step heading already says what this
         is. Several get one each, so the ranges stay told apart. */
      var named = groups.length > 1;

      return (
        (heading ? this.retailHeadHTML(heading) : '') +
        groups
          .map(
            function (group) {
              return (
                (named && group.title ? this.retailHeadHTML(group.title) : '') +
                '<div class="cqo-grid cqo-grid--retail">' +
                group.items.map(this.retailCardHTML.bind(this)).join('') +
                '</div>'
              );
            }.bind(this)
          )
          .join('')
      );
    }

    retailHeadHTML(heading) {
      return (
        '<div class="cqo-sechead cqo-sechead--rule cqo-sechead--spaced">' +
        '<h2 class="cqo-label cqo-label--sm">' + escapeHtml(heading) + '</h2>' +
        '</div>'
      );
    }

    retailStepHTML() {
      /* No sub-head on the retail block itself: the step heading covers it, and
         a multi-collection range labels its own sections. */
      var body =
        this.retailSectionHTML('retail', '', true) +
        this.retailSectionHTML('charts', 'Colour charts', false) +
        this.retailSectionHTML('accessories', 'Accessories', false);

      return '<section class="cqo-step">' + this.stepHeadHTML(3, 'Retail products') + body + '</section>';
    }

    retailCardHTML(item) {
      var onSale = item.compareAt > item.price;
      var margin = onSale ? Math.round((1 - item.price / item.compareAt) * 100) + '% off RRP' : 'Everyday trade price';

      return (
        '<div class="cqo-card cqo-card--retail">' +
        '<span class="cqo-card__shot">' +
        (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.name) + '" loading="lazy" width="400" height="400">' : '') +
        '<code class="cqo-card__shot-empty" data-cqo-fallback' + (item.image ? ' hidden' : '') + '>product shot</code>' +
        (onSale ? '<span class="cqo-flag">Sale</span>' : '') +
        '</span>' +
        '<span class="cqo-card__body">' +
        '<span class="cqo-card__name cqo-card__name--strong">' + escapeHtml(item.name) + '</span>' +
        '<span class="cqo-card__spacer"></span>' +
        (item.tradeOnly
          ? ''
          : '<span class="cqo-card__row cqo-card__row--price">' +
            this.priceHTML(item, 'cqo-price--lg') +
            (onSale ? '<s class="cqo-rrp">RRP ' + this.money(item.compareAt) + '</s>' : '') +
            '</span>' +
            '<span class="cqo-margin">' + margin + '</span>') +
        '<span class="cqo-card__row cqo-card__row--actions">' +
        this.stepperHTML(item, 'lg') +
        this.addSixHTML(item) +
        '</span>' +
        '</span>' +
        '</div>'
      );
    }

    /* ---- Step 5: review & confirmation ---- */

    reviewStepHTML() {
      return (
        '<section class="cqo-step">' +
        this.stepHeadHTML(4, 'Review order') +
        '<div class="cqo-grid cqo-grid--review" data-cqo-review>' + this.reviewCardsHTML() + '</div>' +
        '</section>'
      );
    }

    /**
     * Quantities are editable here rather than shown as "×3" behind an Edit
     * button that sends the shopper back a step — the same stepper as everywhere
     * else, with the totals moving as they change.
     */
    reviewCardsHTML() {
      var groups = [
        { label: 'Colour', kinds: ['colour'], step: 1 },
        { label: 'Developers & lighteners', kinds: ['dev', 'bleach'], step: 2 },
        { label: 'Retail', kinds: RETAIL_KINDS, step: 3 }
      ];

      return groups
        .map(
          function (group) {
            var lines = this.lines(group.kinds);
            var total = lines.reduce(function (sum, line) {
              return sum + line.item.price * line.qty;
            }, 0);
            var count = lines.reduce(function (sum, line) {
              return sum + line.qty;
            }, 0);

            return (
              '<div class="cqo-review">' +
              '<div class="cqo-review__head">' +
              '<span class="cqo-label">' + escapeHtml(group.label) + '</span>' +
              '<span class="cqo-review__count">' + count + ' ' + plural(count, 'item', 'items') + '</span>' +
              '</div>' +
              (lines.length
                ? lines
                    .map(
                      function (line) {
                        var item = line.item;
                        var name = item.kind === 'colour' ? item.code + ' ' + item.name : item.name;
                        return (
                          '<div class="cqo-review__line">' +
                          '<span class="swatch swatch--square cqo-swatch--xs" style="--swatch--background: ' + swatchBackground(item) + ';"></span>' +
                          '<span class="cqo-review__id">' +
                          '<span class="cqo-review__name">' + escapeHtml(name) + '</span>' +
                          '<code class="cqo-code cqo-code--xs">' + escapeHtml(item.sku) + '</code>' +
                          '</span>' +
                          '<span class="cqo-review__qty">' + this.stepperHTML(item, 'sm') + '</span>' +
                          '<span class="cqo-review__total">' + this.money(item.price * line.qty) + '</span>' +
                          '</div>'
                        );
                      }.bind(this)
                    )
                    .join('')
                : '<div class="cqo-review__line cqo-review__line--empty">Nothing added.</div>') +
              '<div class="cqo-review__foot">' +
              '<span class="cqo-review__grand">' + this.money(total) + '</span>' +
              '</div>' +
              '</div>'
            );
          }.bind(this)
        )
        .join('');
    }

    /**
     * Rebuild the review cards in place. A quantity change moves the line total,
     * the group count and the card total, and drops the line altogether at zero,
     * so nudging the inputs is not enough — but a full render would pull the
     * button out from under the shopper's finger, hence the focus restore.
     */
    renderReview() {
      var slot = this.querySelector('[data-cqo-review]');
      if (!slot) return this.render();

      var active = document.activeElement;
      var focusId = active && active.getAttribute ? active.getAttribute('data-cqo-id') : null;
      var focusAction = active && active.getAttribute ? active.getAttribute('data-cqo-action') : null;

      slot.innerHTML = this.reviewCardsHTML();

      if (focusId && focusAction) {
        var again = slot.querySelector('[data-cqo-action="' + focusAction + '"][data-cqo-id="' + focusId + '"]');
        if (again) again.focus({ preventScroll: true });
      }
      this.syncSummary();
    }

    confirmationHTML() {
      var confirmation = this.state.confirmation || { count: 0, total: 0, blocked: 0 };

      return (
        '<section class="cqo-step">' +
        '<div class="cqo-done">' +
        '<span class="cqo-eyebrow">Added to cart</span>' +
        '<span class="cqo-done__metric">' + confirmation.count + ' ' + plural(confirmation.count, 'item', 'items') + ' · ' + this.money(confirmation.total) + '</span>' +
        (confirmation.blocked
          ? '<p class="cqo-hint">' + confirmation.blocked + ' ' + plural(confirmation.blocked, 'line was', 'lines were') + ' left out — trade-only or sold out.</p>'
          : '') +
        '<div class="cqo-done__actions">' +
        '<a class="button" href="/checkout">Go to checkout</a>' +
        '<a class="button button--secondary" href="' + escapeHtml(this.routes.collections || '/collections') + '">Continue shopping</a>' +
        '<button type="button" class="button button--secondary" data-cqo-action="restart">Start another brand</button>' +
        '</div>' +
        '</div>' +
        '</section>'
      );
    }

    /* ---- Order summary (steps 2–4) ---- */

    summaryHTML() {
      var brand = this.brand();
      if (!brand) return '';

      var rows = [
        { label: 'Colour', kinds: ['colour'] },
        { label: 'Developer', kinds: ['dev'] },
        { label: 'Bleach', kinds: ['bleach'] },
        { label: 'Retail', kinds: RETAIL_KINDS }
      ]
        .map(
          function (row) {
            var units = this.units(row.kinds);
            var total = this.sum(row.kinds);
            return (
              '<div class="cqo-summary__row">' +
              '<span class="cqo-summary__label">' + row.label + '</span>' +
              '<span class="cqo-summary__qty" data-cqo-sum-qty="' + row.kinds.join(',') + '">' + (units ? '×' + units : '—') + '</span>' +
              '<span class="cqo-summary__total" data-cqo-sum-total="' + row.kinds.join(',') + '">' + this.money(total) + '</span>' +
              '</div>'
            );
          }.bind(this)
        )
        .join('');

      var logo =
        /* 38px and always in view — lazy would only delay it. */
        (brand.logo ? '<img src="' + escapeHtml(brand.logo) + '" alt="" class="cqo-summary__logo" width="76" height="76">' : '') +
        '<span class="cqo-summary__wordmark" data-cqo-fallback' + (brand.logo ? ' hidden' : '') + '>' + escapeHtml(brand.name) + '</span>';

      return (
        '<div class="cqo-summary__head">' +
        '<span class="cqo-summary__media">' + logo + '</span>' +
        '<span class="cqo-summary__id">' +
        '<span class="cqo-summary__title">Your order</span>' +
        '<span class="cqo-summary__brand">' + escapeHtml(brand.title || brand.name) + '</span>' +
        '</span>' +
        '</div>' +
        rows +
        '<div class="cqo-summary__foot">' +
        '<div class="cqo-summary__subtotal">' +
        '<span class="cqo-label cqo-label--sm">Subtotal</span>' +
        '<span class="cqo-metric" data-cqo-subtotal>' + this.money(this.grandTotal()) + '</span>' +
        '</div>' +
        '<div class="cqo-track"><span class="cqo-track__fill" data-cqo-track style="width:' + this.shippingPercent() + '%"></span></div>' +
        '<span class="cqo-hint" data-cqo-freight>' + escapeHtml(this.freightNote()) + '</span>' +
        (this.state.step < 4
          ? '<button type="button" class="cqo-btn cqo-btn--outline cqo-btn--block" data-cqo-action="next"' +
            (this.isLoading(this.kindsForStep(this.state.step)) ? ' disabled' : '') +
            '>' + NEXT_LABELS[this.state.step] + '</button>'
          : '') +
        '<button type="button" class="cqo-btn cqo-btn--red cqo-btn--block" data-cqo-action="add-all">Add all <span data-cqo-total-items>' + this.grandUnits() + '</span> to cart</button>' +
        '<span class="cqo-hint cqo-hint--centre">Tax included. Shipping calculated at checkout.</span>' +
        '<span class="cqo-error" data-cqo-error role="alert" hidden></span>' +
        '</div>'
      );
    }

    /* ---- Sticky footer bar (steps 2–5) ---- */

    barHTML() {
      var step = this.state.step;

      return (
        '<div class="cqo-bar">' +
        '<button type="button" class="cqo-btn cqo-btn--outline" data-cqo-action="back">Back</button>' +
        '<div class="cqo-bar__counts">' +
        '<span data-cqo-bar-colour>' + this.units(['colour']) + ' ' + plural(this.units(['colour']), 'tube', 'tubes') + '</span>' +
        '<span data-cqo-bar-dev>' + this.units(['dev']) + ' developer · ' + this.units(['bleach']) + ' bleach</span>' +
        '<span data-cqo-bar-retail>' + this.units(RETAIL_KINDS) + ' retail</span>' +
        '</div>' +
        '<span class="cqo-bar__spacer"></span>' +
        '<span class="cqo-bar__total" data-cqo-bar-total>' + this.money(this.grandTotal()) + '</span>' +
        '<button type="button" class="cqo-btn cqo-btn--invert cqo-btn--next" data-cqo-action="next"' +
        (this.isLoading(this.kindsForStep(step)) ? ' disabled' : '') + '>' + NEXT_LABELS[step] + '</button>' +
        '</div>'
      );
    }

    /* -------------------------------------------------------------- *
     * Totals and in-place sync
     * -------------------------------------------------------------- */

    grandTotal() {
      return this.sum(ALL_KINDS);
    }

    grandUnits() {
      return this.units(ALL_KINDS);
    }

    shippingPercent() {
      var threshold = this.settings.freeShippingThreshold || 19900;
      return Math.min(100, Math.round((this.grandTotal() / threshold) * 100));
    }

    freightNote() {
      var threshold = this.settings.freeShippingThreshold || 19900;
      var total = this.grandTotal();
      if (total >= threshold) return 'Free shipping applied · or collect in store';
      return 'Add ' + this.money(threshold - total) + ' for free shipping';
    }

    /** Patch the numbers in place rather than re-rendering a list of 90 cards. */
    syncQuantities() {
      var qty = this.state.qty;

      this.querySelectorAll('[data-cqo-qty]').forEach(function (input) {
        var value = qty[input.getAttribute('data-cqo-id')] || 0;
        if (String(value) !== input.value) input.value = value;
        var wrapper = input.closest('.cqo-qty');
        if (wrapper) wrapper.setAttribute('data-cqo-has-qty', value > 0 ? 'true' : 'false');
        var card = input.closest('[data-cqo-card]');
        if (card) card.setAttribute('data-picked', value > 0 ? 'true' : 'false');
      });

      this.syncCounters();

      /* The review shows its own totals and drops a line at zero, so it has to be
         rebuilt rather than nudged. Reached from the steppers and from a field
         losing focus, never from a keystroke. */
      if (this.state.step === 4 && !this.state.done) this.renderReview();
    }

    /** Everything derived from the quantity map except the steppers themselves. */
    syncCounters() {
      var qty = this.state.qty;

      var tallies = this.querySelectorAll('[data-cqo-group-tally]');
      if (tallies.length) {
        var matched = this.matchedShades();
        tallies.forEach(function (slot) {
          var key = slot.getAttribute('data-cqo-group-tally');
          var picked = matched.reduce(function (total, item) {
            return item.group === key ? total + (qty[item.id] || 0) : total;
          }, 0);
          slot.textContent = picked ? picked + ' ' + plural(picked, 'tube', 'tubes') : '';
        });
      }

      this.querySelectorAll('[data-cqo-panel-tally]').forEach(
        function (slot) {
          var parts = slot.getAttribute('data-cqo-panel-tally').split(':');
          var items =
            parts[0] === 'toggle-brand-dev'
              ? this.itemsFor(parts[1], 'dev')
              : this.bleachPool().filter(function (item) {
                  return (item.vendor || 'Other lighteners') === parts[1];
                });
          var picked = items.reduce(function (total, item) {
            return total + (qty[item.id] || 0);
          }, 0);
          slot.textContent = picked ? picked + ' picked' : '';
        }.bind(this)
      );

      var units = this.querySelector('[data-cqo-colour-units]');
      if (units) units.textContent = this.units(['colour']);
      var colourLines = this.querySelector('[data-cqo-colour-lines]');
      if (colourLines) colourLines.textContent = this.lines(['colour']).length;
      var colourTotal = this.querySelector('[data-cqo-colour-total]');
      if (colourTotal) colourTotal.textContent = this.money(this.sum(['colour']));

      this.syncSummary();
    }

    syncSummary() {
      var total = this.grandTotal();

      this.querySelectorAll('[data-cqo-sum-qty]').forEach(
        function (slot) {
          var kinds = slot.getAttribute('data-cqo-sum-qty').split(',');
          var units = this.units(kinds);
          slot.textContent = units ? '×' + units : '—';
        }.bind(this)
      );

      this.querySelectorAll('[data-cqo-sum-total]').forEach(
        function (slot) {
          slot.textContent = this.money(this.sum(slot.getAttribute('data-cqo-sum-total').split(',')));
        }.bind(this)
      );

      var subtotal = this.querySelector('[data-cqo-subtotal]');
      if (subtotal) subtotal.textContent = this.money(total);

      var track = this.querySelector('[data-cqo-track]');
      if (track) track.style.width = this.shippingPercent() + '%';

      var freight = this.querySelector('[data-cqo-freight]');
      if (freight) freight.textContent = this.freightNote();

      var totalItems = this.querySelector('[data-cqo-total-items]');
      if (totalItems) totalItems.textContent = this.grandUnits();

      var barColour = this.querySelector('[data-cqo-bar-colour]');
      if (barColour) {
        var colourUnits = this.units(['colour']);
        barColour.textContent = colourUnits + ' ' + plural(colourUnits, 'tube', 'tubes');
      }

      var barDev = this.querySelector('[data-cqo-bar-dev]');
      if (barDev) barDev.textContent = this.units(['dev']) + ' developer · ' + this.units(['bleach']) + ' bleach';

      var barRetail = this.querySelector('[data-cqo-bar-retail]');
      if (barRetail) barRetail.textContent = this.units(RETAIL_KINDS) + ' retail';

      var barTotal = this.querySelector('[data-cqo-bar-total]');
      if (barTotal) barTotal.textContent = this.money(total);
    }
  }

  if (!customElements.get('colour-quick-order')) {
    customElements.define('colour-quick-order', ColourQuickOrderElement);
  }
})();
